import { Box } from "@mui/material";
import { Form, Formik } from "formik";
import _ from "lodash";
import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import * as yup from "yup";
import CustomButton from "../../../components/@extended/CustomButton";
import ConfirmationDialog from "../../../components/common/ConfirmationDialog";
import DependsOnValidationDialog from "../../../components/common/DependsOnValidationDialog";
import FocusError from "../../../components/common/FocusError";
import OTPPanel from "../../../components/common/OTPPanel";
import QuizDrawer from "../../../components/common/QuizDrawer";
import { useResponsive } from "../../../hooks/ResponsiveProvider";
import { bloqcibeApi, useGenerateOTPValueMutation, useVerifyOTPValueMutation } from "../../../store/slices/apiSlice";
import { openBackdropLoading } from "../../../store/slices/applicationSlice";
import { openMessage } from "../../../store/slices/showMessageSlice";
import { setUserLocation } from "../../../store/slices/userDetailsSlice";
import { phoneValidationSchema } from "../FormFields/PhoneInputField";
import { validateDependsOnConstraints, getEmptyValueForFieldType } from "../utils/dependsOnValidationService";
import { validateAtLeastOneWhen } from "../utils/atLeastOneValidationService";
import {
  checkConfirmationValidations,
  getEffectiveConfirmationConfig,
} from "../utils/confirmationValidationService";
import {
  isNewValidationEnabled,
  validate5AEndDate,
  validate5AsaStartDate,
  validateAgeAtIndexDate,
  validateAgeCriteria,
  validateDateAfter26WeeksIndex,
  validateDateAfterIndexAndInitiation,
  validateDateAfterVDZDiscontinuation,
  validateDateAfterVDZDiscontinuationAndInitiation,
  validateDateAtWeeksAfterIndex,
  validateDateFromDiagnosisTo52WeeksPostIndex,
  validateDateOfDiagnosis,
  validateDateWithin52WeeksPlus28DaysPostIndex,
  validateDateOfDischarge,
  validateDateOfEnrolment,
  validateDateOfProcedure,
  validateDateRangeAfterIndex,
  validateDateRangeBeforeIndex,
  validateDateRangeDiagnosisToDiagnosisPlus6Months,
  validateDateRangeIndexMinus12Months,
  validateDateRangeIndexMinus1Month,
  validateDateRangeIndexMinus6Months,
  validateDateWithin1WeekOfIndex,
  validateDateWithin6MonthsOfIndex,
  validateDiagnosedWithCDin6months,
  validateDurationDisease,
  validateEligibilityConfirmation,
  validateHbiAssessmentConfirmation,
  validateIndexDateBetweenJan2016AndFeb2024,
  validateMostRecentDate,
  validatePatientsWithNoCDSTScore,
  validatePriorExposure,
  validateSeverityHbi,
  validateStopDateAfterIndexAndStartDate,
  validateYearOfDiagnosis
} from "../utils/validationService";
import DynamicCategory from "./DynamicCategory";
import DynamicFields from "./DynamicFields";
import DynamicFormAction from "./DynamicFormAction";
import { SUBJECT_STATUSES } from "../../../util/constants";

/**
 * Merge error objects, combining nested structures from both sources
 * DependsOn validation errors take precedence over existing errors for the same field
 * 
 * @param {Object} existingErrors - Existing Formik errors
 * @param {Object} newErrors - New errors from dependsOn validation
 * @returns {Object} - Merged error object
 */
const mergeErrors = (existingErrors, newErrors) => {
  if (!existingErrors && !newErrors) {
    return {};
  }
  if (!existingErrors) {
    return newErrors || {};
  }
  if (!newErrors) {
    return existingErrors || {};
  }

  const merged = { ...existingErrors };

  // Merge new errors, with new errors taking precedence
  Object.keys(newErrors).forEach((key) => {
    if (typeof newErrors[key] === 'object' && newErrors[key] !== null && !Array.isArray(newErrors[key])) {
      // Nested error structure (category.field)
      if (!merged[key]) {
        merged[key] = {};
      }
      merged[key] = { ...merged[key], ...newErrors[key] };
    } else {
      // Leaf error - new error takes precedence
      merged[key] = newErrors[key];
    }
  });

  return merged;
};

const isDateValue = (value) => {
  // Simple regex to check if the value is in the format of "DD/MM/YYYY HH:MM:SS"
  return /^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}:\d{2}$/.test(value);
};

const compareObjects = (initialObject, updatedObject) => {
  if (!initialObject || !updatedObject) return {};
  const changes = {};

  const compare = (obj1, obj2, parentKey = '') => {
    for (const key in obj1) {
      if (typeof obj1[key] === 'object' && obj1[key] !== null && !Array.isArray(obj1[key])) {
        compare(obj1[key], obj2[key], `${parentKey}${key}.`);
      } else {
        if (
          obj1[key] !== '' &&
          obj1[key] !== null &&
          obj1[key] !== obj2[key] &&
          !isDateValue(obj1[key]) &&
          typeof obj1[key] !== 'boolean'
        ) {
          if (Array.isArray(obj1[key]) && Array.isArray(obj2[key])) {
            const diffInObj1 = obj1[key].some((ele) => !obj2[key].includes(ele));
            const diffInObj2 = obj2[key].some((ele) => !obj1[key].includes(ele));
            if (diffInObj1 || diffInObj2) {
              // changes[`${parentKey}${key}`] = { initial: obj1[key], updated: obj2[key] };
            }
          } else {
            changes[`${parentKey}${key}`] = { initial: obj1[key], updated: obj2[key] };
          }
        }
      }
    }
  };

  compare(initialObject, updatedObject);

  return changes;
};


const getFieldValidationSchema = (fieldValidationSchema, fe, validationContext = null) => {
  const skipValidationFields = ['MULTISELECT_DROP_DOWN', 'CHECK_BOX_GROUP', 'LABEL', 'DATA_LABEL', 'CHECK_BOX', 'DATA_HTML', 'FILE_UPLOAD', 'BUTTON', "SEARCH_DROP_DOWN", "REMOVE_FIELD_BUTTON", "PHONE_INPUT", "DOCUMENT_LINK", 'VIDEO_LINK','EMBED_VIDEO'];
  
  // Skip UI alignment fields: testSpace and divider (by key or type)
  if (fe.key === "divider" || fe.key === "testSpace" || fe.type === "divider" || fe.type === "testSpace") {
    return; // Skip these UI-only fields completely
  }
  
  // Check if field has nested fields and if any of them are not in skipValidationFields
  const hasValidNestedFields = fe.fields && fe.fields.length > 0 && 
    fe.fields.some(nestedField => !skipValidationFields.includes(nestedField.type));
  
  const displayOnlyContainerTypes = ['LABEL', 'DATA_LABEL'];
  // LABEL/DATA_LABEL containers may wrap nested inputs; validate nested fields only, not the container key
  const shouldProcessAsNormalField = !skipValidationFields.includes(fe.type) || hasValidNestedFields;
  const shouldValidateFieldValue =
    shouldProcessAsNormalField && !displayOnlyContainerTypes.includes(fe.type);

  if (shouldValidateFieldValue) {
    let validator = fe.subtype === "number" ? yup.number() : yup.string()
      .transform((value, originalValue) => {
        // Handle non-string values to prevent .trim() errors
        if (originalValue === null || originalValue === undefined) {
          return '';
        }
        if (typeof originalValue !== 'string') {
          return String(originalValue);
        }
        return originalValue;
      })
      .trim();
    if (fe.subtype === "DATE") {
      validator = yup.string()
        .test('is-valid-date', 'Invalid date format', (value) => {
          if(value === 'Invalid Date'){
            return moment(value, 'DD/MM/YYYY', true).isValid();
          }
          else{
            return true
          }
        })
    }
    if (fe.type === "YEAR_PICKER") {
      validator = yup.string()
        .test('is-valid-year', 'Invalid year format', (value) => {
          if (!value) {
            return true; // Empty values handled by required validation
          }
          // Validate year format YYYY (4 digits)
          const yearRegex = /^\d{4}$/;
          if (!yearRegex.test(value)) {
            return false;
          }
          const year = parseInt(value, 10);
          // Validate year is in a reasonable range (e.g., 1900-2100)
          return year >= 1900 && year <= 2100;
        })
    }
    if (fe.required) {
      let req = "required";
      let msg = "This field is required";
      validator = validator[req](msg);
    }
    const requiredDependsOnFieldsArray = fe.requiredDependsOnFields || fe.requiredDependOnFields;
    if (fe.requiredDepends || requiredDependsOnFieldsArray) {
      if (requiredDependsOnFieldsArray && Array.isArray(requiredDependsOnFieldsArray)) {
        requiredDependsOnFieldsArray.forEach((field) => {
          validator = validator.test("conditional-required", "This field is required", function (value, context) {
            const dependsKey = field.requiredDepends && field.requiredDepends.includes(".")
              ? field.requiredDepends.split(".")
              : [field.requiredDepends];
            let dependsValue;
            if (dependsKey.length === 2 && context.parent && (context.parent[dependsKey[1]] !== undefined || Object.prototype.hasOwnProperty.call(context.parent, dependsKey[1]))) {
              dependsValue = context.parent[dependsKey[1]];
            } else {
              const formValues = context.parent || context.options.context || {};
              dependsValue = dependsKey.length === 2 ? formValues[dependsKey[0]]?.[dependsKey[1]] : formValues[dependsKey];
            }

            // Check if requiredValidationValue is provided - if so, use exact match validation
            // If not provided, check if any value exists (truthy check)
            // Use isEmpty check that allows 0 and "0" (number fields)
            const isEmpty = value === undefined || value === null || value === '';
            if (field.requiredValidationValue !== undefined && field.requiredValidationValue !== null && field.requiredValidationValue !== '') {
              if (dependsValue === field.requiredValidationValue) {
                if (isEmpty) {
                  return this.createError({
                        message: "This field is required"
                    });
                }
              }
            } else {
              // If requiredValidationValue is not provided, check if any value exists
              if (dependsValue !== undefined && dependsValue !== null && dependsValue !== '') {
                if (isEmpty) {
                  return this.createError({
                        message: "This field is required"
                    });
                }
              }
            }
            return true;
          });
        });
      } else {
        validator = validator.test("conditional-required", "This field is required", function (value, context) {
          const dependsKey = fe.requiredDepends && fe.requiredDepends.includes(".")
            ? fe.requiredDepends.split(".")
            : [fe.requiredDepends];
          let dependsValue;
          if (dependsKey.length === 2 && context.parent && (context.parent[dependsKey[1]] !== undefined || Object.prototype.hasOwnProperty.call(context.parent, dependsKey[1]))) {
            dependsValue = context.parent[dependsKey[1]];
          } else {
            const formValues = context.parent || context.options.context || {};
            dependsValue = dependsKey.length === 2 ? formValues[dependsKey[0]]?.[dependsKey[1]] : formValues[dependsKey];
          }

          // Check if requiredValidationValue is provided - if so, use exact match validation
          // If not provided, check if any value exists (truthy check)
          // Use isEmpty check that allows 0 and "0" (number fields)
          const isEmpty = value === undefined || value === null || value === '';
          if (fe.requiredValidationValue !== undefined && fe.requiredValidationValue !== null && fe.requiredValidationValue !== '') {
            if (dependsValue === fe.requiredValidationValue) {
              if (isEmpty) {
                    return this.createError({
                        message: "This field is required"
                    });
                }
            }
          } else {
            // If requiredValidationValue is not provided, check if any value exists
            if (dependsValue !== undefined && dependsValue !== null && dependsValue !== '') {
              if (isEmpty) {
                    return this.createError({
                        message: "This field is required"
                    });
                }
            }
          }
          return true;
        });
      }
    }
    if (fe.validations) {
      fe.validations.forEach((validation) => {
        const { params, type } = validation;
        switch (type) {
          case 'sameAs':
            validator = validator.test('fieldMatch', params[1], function (value, context) {
              // Use context.parent to access root form values, fallback to context.options.context for root-level fields
              const formValues = context.options.context || context.parent;
              const fieldKey = params[0].includes('.') ? params[0].split('.') : params[0];
              const fieldValue = params[0].includes('.')
                ? formValues[fieldKey[0]]?.[fieldKey[1]]
                : formValues[fieldKey];
              if (!value) {
                return this.createError({
                  message: 'This field is required',
                });
              }
              if (value !== fieldValue) {
                return this.createError({
                  message: params[1],
                });
              }
              return true;
            });
            break;
          case 'dateRange':
          // params: [minDate, maxDate, errorMessage]
          validator = validator.test('date-range', params[2] || 'Date is out of allowed range', function (value) {
            if (!value) return true; // skip if empty, let required handle

            const momentValue = moment(value, 'DD/MM/YYYY', true);
            const minDate = params[0] ? moment(params[0], 'DD/MM/YYYY', true) : null;
            const maxDate = params[1] ? moment(params[1], 'DD/MM/YYYY', true) : null;
            if (!momentValue.isValid()) {
              return this.createError({
                message: 'Invalid date format',
              });
            }

            if (minDate && momentValue.isBefore(minDate, 'day')) {
              return this.createError({
                message: params[2] || `Date should not be before ${params[0]}`,
              });
            }
            if (maxDate && momentValue.isAfter(maxDate, 'day')) {
              return this.createError({
                message: params[2] || `Date should not be after ${params[1]}`,
              });
            }
            return true;
          });
            break;
          case 'startDate':
            // Validation for start date field
            // params: [stopDateFieldKey, errorMessage]
            // Validates that start date is not greater than stop date
            validator = validator.test('start-date', params[1] || 'Start date must not be greater than stop date', function (value, context) {
              if (!value) return true; // skip if empty, let required handle

              if (!params[0]) {
                return true; // If no stop date field key provided, skip validation
              }

              // Use context.parent to access root form values, fallback to context.options.context for root-level fields
              const formValues = context.parent || context.options.context;
              
              // Get the stop date field value
              const stopDateFieldPath = params[0];
              const stopDateFieldKey = stopDateFieldPath.includes('.') ? stopDateFieldPath.split('.') : stopDateFieldPath;
              const stopDateValue = stopDateFieldPath.includes('.')
                ? formValues[stopDateFieldKey[0]]?.[stopDateFieldKey[1]]
                : formValues[stopDateFieldKey];

              if (!stopDateValue) {
                return true; // If stop date field is empty, skip validation
              }

              const startDate = moment(value, 'DD/MM/YYYY', true);
              const stopDate = moment(stopDateValue, 'DD/MM/YYYY', true);

              if (!startDate.isValid() || !stopDate.isValid()) {
                return this.createError({
                  message: 'Invalid date format',
                });
              }

              // Validate: start date should not be greater than stop date
              if (startDate.isAfter(stopDate, 'day')) {
                return this.createError({
                  message: params[1] || 'Start date must not be greater than stop date',
                });
              }

              return true;
            });
            break;
          case 'stopDate':
            // Validation for stop date field
            // params: [startDateFieldKey, errorMessage]
            // Validates that stop date is not smaller than start date
            validator = validator.test('stop-date', params[1] || 'Stop date must not be smaller than start date', function (value, context) {
              if (!value) return true; // skip if empty, let required handle

              if (!params[0]) {
                return true; // If no start date field key provided, skip validation
              }

              // Use context.parent to access root form values, fallback to context.options.context for root-level fields
              const formValues = context.parent || context.options.context;
              
              // Get the start date field value
              const startDateFieldPath = params[0];
              const startDateFieldKey = startDateFieldPath.includes('.') ? startDateFieldPath.split('.') : startDateFieldPath;
              const startDateValue = startDateFieldPath.includes('.')
                ? formValues[startDateFieldKey[0]]?.[startDateFieldKey[1]]
                : formValues[startDateFieldKey];

              if (!startDateValue) {
                return true; // If start date field is empty, skip validation
              }

              const stopDate = moment(value, 'DD/MM/YYYY', true);
              const startDate = moment(startDateValue, 'DD/MM/YYYY', true);

              if (!stopDate.isValid() || !startDate.isValid()) {
                return this.createError({
                  message: 'Invalid date format',
                });
              }

              // Validate: stop date should not be smaller than start date
              if (stopDate.isBefore(startDate, 'day')) {
                return this.createError({
                  message: params[1] || 'Stop date must not be smaller than start date',
                });
              }

              return true;
            });
            break;
          case 'dateRangeAfterIndex':
            const fieldConfig = fe.fieldConfig || fe;
            validator = validator.test(
              'date-range-after-index',
              'Date must be after index date.',
              async function (value, context) {
                if (!value) {
                  return true; // Empty values handled by required validation
                }
  
                try {
                  const isValid = await validateDateRangeAfterIndex(
                    value,
                    fieldConfig || fe,
                    context.options.context,
                    validationContext,
                    validationContext.getSubjectAnswers
                  );
  
                  if (isValid !== true) {
                    const errorMessage =
                      typeof isValid === 'object' && isValid?.message
                        ? isValid.message
                        : `Date must be after index date.`;
                    return this.createError({ message: errorMessage });
                  }
                  return true;
                } catch (error) {
                  return this.createError({
                    message: 'Unable to validate date range',
                  });
                }
              }
            );
            
          break;
          case 'dateRangeBeforeIndex':
            const fieldConfigBeforeIndex = fe.fieldConfig || fe;
            validator = validator.test(
              'date-range-before-index',
              'Date must be before index date.',
              async function (value, context) {
                if (!value) {
                  return true; // Empty values handled by required validation
                }

                try {
                  const isValid = await validateDateRangeBeforeIndex(
                    value,
                    fieldConfigBeforeIndex || fe,
                    context.options.context,
                    validationContext,
                    validationContext.getSubjectAnswers
                  );

                  if (isValid !== true) {
                    const errorMessage =
                      typeof isValid === 'object' && isValid?.message
                        ? isValid.message
                        : `Date must be before index date.`;
                    return this.createError({ message: errorMessage });
                  }
                  return true;
                } catch (error) {
                  return this.createError({
                    message: 'Unable to validate date range',
                  });
                }
              }
            );
            
            break;
          case 'stopDateAfterIndexAndStartDate':
            const fieldConfigStopDate = fe.fieldConfig || fe;
            validator = validator.test(
              'stop-date-after-index-and-start-date',
              'Stop date must be on or after both index date and start date.',
              async function (value, context) {
                if (!value) {
                  return true; // Empty values handled by required validation
                }

                try {
                  const isValid = await validateStopDateAfterIndexAndStartDate(
                    value,
                    fieldConfigStopDate || fe,
                    context.options.context,
                    validationContext,
                    validationContext.getSubjectAnswers
                  );

                  if (isValid !== true) {
                    const errorMessage =
                      typeof isValid === 'object' && isValid?.message
                        ? isValid.message
                        : `Stop date must be on or after both index date and start date.`;
                    return this.createError({ message: errorMessage });
                  }
                  return true;
                } catch (error) {
                  return this.createError({
                    message: 'Unable to validate stop date',
                  });
                }
              }
            );
            
            break;
          case 'dateRangeDiagnosisToDiagnosisPlus6Months':
            const fieldConfigDiagnosis = fe.fieldConfig || fe;
            validator = validator.test(
              'date-range-diagnosis-to-diagnosis-plus-6-months',
              params[0] || 'Date must be between date of diagnosis and (date of diagnosis + 6 months)',
              async function (value, context) {
                if (!value) {
                  return true;
                }

                try {
                  const isValid = await validateDateRangeDiagnosisToDiagnosisPlus6Months(
                    value,
                    fieldConfigDiagnosis,
                    context.parent || context.options.context,
                    validationContext,
                    validationContext.getSubjectAnswers
                  );

                  if (isValid !== true) {
                    const errorMessage =
                      typeof isValid === 'object' && isValid?.message
                        ? isValid.message
                        : params[0] || 'Date must be between date of diagnosis and (date of diagnosis + 6 months)';
                    return this.createError({ message: errorMessage });
                  }
                  return true;
                } catch (error) {
                  console.error('[DynamicForm] Yup test: dateRangeDiagnosisToDiagnosisPlus6Months exception -', error);
                  return this.createError({
                    message: 'Unable to validate date range',
                  });
                }
              }
            );
            break;

          case 'dateRangeIndexMinus6Months':
            const fieldConfigMinus6Months = fe.fieldConfig || fe;
            validator = validator.test(
              'date-range-index-minus-6-months',
              params[0] || 'Date must be between (Index date – 6 months) and index date',
              async function (value, context) {
                if (!value) {
                  return true;
                }

                try {
                  const isValid = await validateDateRangeIndexMinus6Months(
                    value,
                    fieldConfigMinus6Months,
                    context.parent || context.options.context,
                    validationContext,
                    validationContext.getSubjectAnswers
                  );

                  if (isValid !== true) {
                    const errorMessage =
                      typeof isValid === 'object' && isValid?.message
                        ? isValid.message
                        : params[0] || 'Date must be between (Index date – 6 months) and index date';
                    return this.createError({ message: errorMessage });
                  }
                  return true;
                } catch (error) {
                  console.error('[DynamicForm] Yup test: dateRangeIndexMinus6Months exception -', error);
                  return this.createError({
                    message: 'Unable to validate date range',
                  });
                }
              }
            );
            break;

          case 'dateAtWeeksAfterIndex':
            const fieldConfigWeeks = fe.fieldConfig || fe;
            // params[0] = custom error message (optional)
            // fieldConfig.targetWeeks = array of weeks (default: [10, 14, 26, 52])
            // fieldConfig.toleranceDays = tolerance in days (default: 7)
            validator = validator.test(
              'date-at-weeks-after-index',
              params[0] || 'Date must be approximately at week 10, 14, 26, or 52 after index date',
              async function (value, context) {
                if (!value) {
                  return true;
                }

                try {
                  // Merge targetWeeks from params if provided
                  const targetWeeks = params[1] || fieldConfigWeeks?.targetWeeks || [10, 14, 26, 52];
                  const toleranceDays = params[2] || fieldConfigWeeks?.toleranceDays || 7;
                  
                  const fieldConfigWithWeeks = {
                    ...fieldConfigWeeks,
                    targetWeeks,
                    toleranceDays,
                  };

                  const isValid = await validateDateAtWeeksAfterIndex(
                    value,
                    fieldConfigWithWeeks,
                    context.parent || context.options.context,
                    validationContext,
                    validationContext.getSubjectAnswers
                  );

                  if (isValid !== true) {
                    const errorMessage =
                      typeof isValid === 'object' && isValid?.message
                        ? isValid.message
                        : params[0] || 'Date must be approximately at week 10, 14, 26, or 52 after index date';
                    return this.createError({ message: errorMessage });
                  }
                  return true;
                } catch (error) {
                  console.error('[DynamicForm] Yup test: dateAtWeeksAfterIndex exception -', error);
                  return this.createError({
                    message: 'Unable to validate date at weeks after index date',
                  });
                }
              }
            );
            break;

          case 'dateRangeIndexMinus12Months':
            const fieldConfigMinus12Months = fe.fieldConfig || fe;
            validator = validator.test(
              'date-range-index-minus-12-months',
              params[0] || 'Date must be between (Index date – 12 months) and index date',
              async function (value, context) {
                if (!value) {
                  return true;
                }

                try {
                  const isValid = await validateDateRangeIndexMinus12Months(
                    value,
                    fieldConfigMinus12Months,
                    context.parent || context.options.context,
                    validationContext,
                    validationContext.getSubjectAnswers
                  );

                  if (isValid !== true) {
                    const errorMessage =
                      typeof isValid === 'object' && isValid?.message
                        ? isValid.message
                        : params[0] || 'Date must be between (Index date – 12 months) and index date';
                    return this.createError({ message: errorMessage });
                  }
                  return true;
                } catch (error) {
                  console.error('[DynamicForm] Yup test: dateRangeIndexMinus12Months exception -', error);
                  return this.createError({
                    message: 'Unable to validate date range',
                  });
                }
              }
            );
            break;
          
          case 'dateAfterVDZDiscontinuation':
            const fieldConfigVDZDiscontinuation = fe.fieldConfig || fe;
            
            validator = validator.test('', 'Date must be after date of VDZ discontinuation' || params[1], async function (value, context) {
              if (!value) {
                return true;
              }

              try {
                const isValid = await validateDateAfterVDZDiscontinuation(
                  value,
                  fieldConfigVDZDiscontinuation,
                  context.parent || context.options.context,
                  validationContext,
                  validationContext.getSubjectAnswers
                );

                if (isValid !== true) {
                  const errorMessage =
                    typeof isValid === 'object' && isValid?.message
                      ? isValid.message
                      : 'Date must be after date of VDZ discontinuation';
                  return this.createError({ message: errorMessage });
                }
                return true;
              } catch (error) {
                console.error('[DynamicForm] Yup test: dateAfterVDZDiscontinuation exception -', error);
                return this.createError({
                  message: 'Unable to validate date after VDZ discontinuation',
                });
              }

            })
            break

          case 'dateAfterVDZDiscontinuationAndInitiation':
            const fieldConfigVDZDiscontinuationAndInit = fe.fieldConfig || fe;
            
            validator = validator.test(
              'date-after-vdz-discontinuation-and-initiation',
              params[0] || 'Date must be after VDZ discontinuation date and date of initiation',
              async function (value, context) {
                if (!value) {
                  return true;
                }

                try {
                  const isValid = await validateDateAfterVDZDiscontinuationAndInitiation(
                    value,
                    fieldConfigVDZDiscontinuationAndInit,
                    context.parent || context.options.context,
                    validationContext,
                    validationContext.getSubjectAnswers
                  );

                  if (isValid !== true) {
                    const errorMessage =
                      typeof isValid === 'object' && isValid?.message
                        ? isValid.message
                        : params[0] || 'Date must be after VDZ discontinuation date and date of initiation';
                    return this.createError({ message: errorMessage });
                  }
                  return true;
                } catch (error) {
                  console.error('[DynamicForm] Yup test: dateAfterVDZDiscontinuationAndInitiation exception -', error);
                  return this.createError({
                    message: 'Unable to validate date after VDZ discontinuation and initiation',
                  });
                }
              }
            );
            break;

          case 'dateAfterIndexAndInitiation':
            const fieldConfigAfterIndexInit = fe.fieldConfig || fe;
            validator = validator.test(
              'date-after-index-and-initiation',
              params[0] || 'Date must be after index date and date of initiation',
              async function (value, context) {
                if (!value) {
                  return true;
                }

                try {
                  const isValid = await validateDateAfterIndexAndInitiation(
                    value,
                    fieldConfigAfterIndexInit,
                    context.parent || context.options.context,
                    validationContext,
                    validationContext.getSubjectAnswers
                  );

                  if (isValid !== true) {
                    const errorMessage =
                      typeof isValid === 'object' && isValid?.message
                        ? isValid.message
                        : params[0] || 'Date must be after index date and date of initiation';
                    return this.createError({ message: errorMessage });
                  }
                  return true;
                } catch (error) {
                  console.error('[DynamicForm] Yup test: dateAfterIndexAndInitiation exception -', error);
                  return this.createError({
                    message: 'Unable to validate date after index and initiation',
                  });
                }
              }
            );
            break;

          case 'dateFromDiagnosisTo52WeeksPostIndex':
            const fieldConfigDiagnosisTo52Weeks = fe.fieldConfig || fe;
            validator = validator.test(
              'date-from-diagnosis-to-52-weeks-post-index',
              params[0] || 'Date must be from date of diagnosis up to 52 weeks post-index date',
              async function (value, context) {
                if (!value) {
                  return true;
                }

                try {
                  const isValid = await validateDateFromDiagnosisTo52WeeksPostIndex(
                    value,
                    fieldConfigDiagnosisTo52Weeks,
                    context.parent || context.options.context,
                    validationContext,
                    validationContext.getSubjectAnswers
                  );

                  if (isValid !== true) {
                    const errorMessage =
                      typeof isValid === 'object' && isValid?.message
                        ? isValid.message
                        : params[0] || 'Date must be from date of diagnosis up to 52 weeks post-index date';
                    return this.createError({ message: errorMessage });
                  }
                  return true;
                } catch (error) {
                  console.error('[DynamicForm] Yup test: dateFromDiagnosisTo52WeeksPostIndex exception -', error);
                  return this.createError({
                    message: 'Unable to validate date range',
                  });
                }
              }
            );
            break;

          case 'dateWithin52WeeksPlus28DaysPostIndex':
            const fieldConfig52Weeks28Days = fe.fieldConfig || fe;
            validator = validator.test(
              'date-within-52-weeks-plus-28-days-post-index',
              params[0] || 'Date must be between index date and 52 weeks + 28 days after index date',
              async function (value, context) {
                if (!value) {
                  return true;
                }

                try {
                  const isValid = await validateDateWithin52WeeksPlus28DaysPostIndex(
                    value,
                    fieldConfig52Weeks28Days,
                    context.parent || context.options.context,
                    validationContext,
                    validationContext.getSubjectAnswers
                  );

                  if (isValid !== true) {
                    const errorMessage =
                      typeof isValid === 'object' && isValid?.message
                        ? isValid.message
                        : params[0] || 'Date must be between index date and 52 weeks + 28 days after index date';
                    return this.createError({ message: errorMessage });
                  }
                  return true;
                } catch (error) {
                  console.error('[DynamicForm] Yup test: dateWithin52WeeksPlus28DaysPostIndex exception -', error);
                  return this.createError({
                    message: 'Unable to validate date range',
                  });
                }
              }
            );
            break;

          case 'dateOfProcedure':
            const fieldConfigProcedure = fe.fieldConfig || fe;
            validator = validator.test(
              'date-of-procedure',
              params[0] || 'Date must be from date of diagnosis up to 52 weeks post-index date (+ 28 days)',
              async function (value, context) {
                if (!value) {
                  return true;
                }

                try {
                  const isValid = await validateDateOfProcedure(
                    value,
                    fieldConfigProcedure,
                    context.parent || context.options.context,
                    validationContext,
                    validationContext.getSubjectAnswers
                  );

                  if (isValid !== true) {
                    const errorMessage =
                      typeof isValid === 'object' && isValid?.message
                        ? isValid.message
                        : params[0] || 'Date must be from date of diagnosis up to 52 weeks post-index date (+ 28 days)';
                    return this.createError({ message: errorMessage });
                  }
                  return true;
                } catch (error) {
                  console.error('[DynamicForm] Yup test: dateOfProcedure exception -', error);
                  return this.createError({
                    message: 'Unable to validate date of procedure',
                  });
                }
              }
            );
            break;


          case 'dateOfDischarge':
            const fieldConfigDischarge = fe.fieldConfig || fe;
            validator = validator.test(
              'date-of-discharge',
              params[0] || 'Date must be after date of admission and no later than 52 weeks post-index date',
              async function (value, context) {
                if (!value) {
                  return true;
                }

                try {
                  const isValid = await validateDateOfDischarge(
                    value,
                    fieldConfigDischarge,
                    context.parent || context.options.context,
                    validationContext,
                    validationContext.getSubjectAnswers
                  );

                  if (isValid !== true) {
                    const errorMessage =
                      typeof isValid === 'object' && isValid?.message
                        ? isValid.message
                        : params[0] || 'Date must be after date of admission and no later than 52 weeks post-index date';
                    return this.createError({ message: errorMessage });
                  }
                  return true;
                } catch (error) {
                  console.error('[DynamicForm] Yup test: dateOfDischarge exception -', error);
                  return this.createError({
                    message: 'Unable to validate date of discharge',
                  });
                }
              }
            );
            break;
          
          case 'dateRangeIndexMinus1Month':
            const fieldConfigMinus1Month = fe.fieldConfig || fe;
            validator = validator.test(
              'date-range-index-minus-1-month',
              'Date must be between (Index date – 1 month) and index date',
              async function (value, context) {
                if (!value) {
                  return true; // Empty values handled by required validation
                }
  
                try {
                  const isValid = await validateDateRangeIndexMinus1Month(
                    value,
                    fieldConfigMinus1Month,
                    context.options.context,
                    validationContext,
                    validationContext.getSubjectAnswers
                  );
  
                  if (isValid !== true) {
                    const errorMessage =
                      typeof isValid === 'object' && isValid?.message
                        ? isValid.message
                        : 'Date must be between (Index date – 1 month) and index date';
                    return this.createError({ message: errorMessage });
                  }
                  return true;
                } catch (error) {
                  console.error('[DynamicForm] Yup test: dateRangeIndexMinus1Month exception -', error);
                  return this.createError({
                    message: 'Unable to validate date range',
                  });
                }
              }
            );
            break;
          
          case 'mostRecentDate':
            const fieldConfigMostRecent = fe.fieldConfig || fe;
            validator = validator.test(
              'most-recent-date-range',
              'Most recent CD characteristics date must be between diagnosis date and just before index date',
              async function (value, context) {
                if (!value) {
                  return true; // Empty values handled by required validation
                }
  
                try {
                  const isValid = await validateMostRecentDate(
                    value,
                    fieldConfigMostRecent,
                    context.parent || context.options.context,
                    validationContext,
                    validationContext.getSubjectAnswers
                  );
  
                  if (isValid !== true) {
                    const errorMessage =
                      typeof isValid === 'object' && isValid?.message
                        ? isValid.message
                        : 'Most recent CD characteristics date must be between diagnosis date and just before index date';
                    return this.createError({ message: errorMessage });
                  }
                  return true;
                } catch (error) {
                  console.error('[DynamicForm] Yup test: mostRecentDate exception -', error);
                  return this.createError({
                    message: 'Unable to validate most recent CD characteristics date',
                  });
                }
              }
            );
            break;

          case 'dateWithin1WeekOfIndex':
            const fieldConfigWithin1Week = fe.fieldConfig || fe;
            validator = validator.test(
              'date-within-1-week-of-index',
              params[0] || 'Date must be within ±1 week (±7 days) of index date',
              async function (value, context) {
                if (!value) {
                  return true; 
                }

                try {
                  // Allow custom tolerance days from params or fieldConfig
                  const toleranceDays = params[1] || fieldConfigWithin1Week?.toleranceDays || 7;
                  const fieldConfigWithTolerance = {
                    ...fieldConfigWithin1Week,
                    toleranceDays,
                  };

                  const isValid = await validateDateWithin1WeekOfIndex(
                    value,
                    fieldConfigWithTolerance,
                    context.parent || context.options.context,
                    validationContext,
                    validationContext.getSubjectAnswers
                  );

                  if (isValid !== true) {
                    const errorMessage =
                      typeof isValid === 'object' && isValid?.message
                        ? isValid.message
                        : params[0] || 'Date must be within ±1 week (±7 days) of index date';
                    return this.createError({ message: errorMessage });
                  }
                  return true;
                } catch (error) {
                  console.error('[DynamicForm] Yup test: dateWithin1WeekOfIndex exception -', error);
                  return this.createError({
                    message: 'Unable to validate date within 1 week of index date',
                  });
                }
              }
            );
            break;

          default:
            // Call the validator's method dynamically, if allowed
            if (!validator[type])
              if (type !== 'sameAs' && type !== 'dateRange' && type !== 'startDate' && type !== 'stopDate' && 
                  type !== 'dateRangeDiagnosisToDiagnosisPlus6Months' && type !== 'dateRangeIndexMinus6Months' && 
                  type !== 'dateAtWeeksAfterIndex' && type !== 'dateRangeIndexMinus12Months' && type !== 'dateAfterIndexAndInitiation' &&
                  type !== 'dateFromDiagnosisTo52WeeksPostIndex' && type !== 'dateOfProcedure' && 
                  type !== 'dateOfAdmission' && type !== 'dateOfDischarge' && type !== 'dateWithin1WeekOfIndex') {
                return;
              }
          validator = validator[type](...params);
            break;
        }
      })
    }

    // Also check for startDateKey/stopDateKey configuration at field level (not in validations array)
    // This allows date fields to validate against each other
    // Configuration:
    // - If field has stopDateKey, it's the START date and should not be > stop date
    // - If field has startDateKey, it's the STOP date and should not be < start date
    if (fe.subtype === 'DATE' && (fe.startDateKey || fe.stopDateKey)) {
      validator = validator.test('start-stop-date-field-config', 'Date range validation failed', function (value, context) {
        if (!value) return true; // skip if empty, let required handle

        // Use context.parent to access root form values, fallback to context.options.context for root-level fields
        const formValues = context.options.context || context.parent;
        
        let compareFieldPath = null;
        let isStartDateField = false;
        
        // Determine which field to compare against
        if (fe.stopDateKey) {
          // This field has stopDateKey, so it's the START date field
          // Compare against the stop date field
          isStartDateField = true;
          compareFieldPath = fe.stopDateKey;
        } else if (fe.startDateKey) {
          // This field has startDateKey, so it's the STOP date field
          // Compare against the start date field
          isStartDateField = false;
          compareFieldPath = fe.startDateKey;
        }

        if (!compareFieldPath) {
          return true; // If no comparison field configured, skip validation
        }

        // Get the comparison field value
        // Handle both nested (category.field) and flat field paths
        const compareFieldKey = compareFieldPath.includes('.') ? compareFieldPath.split('.') : compareFieldPath;
        const compareValue = compareFieldPath.includes('.')
          ? formValues[compareFieldKey[0]]?.[compareFieldKey[1]]
          : formValues[compareFieldKey];

        if (!compareValue) {
          return true; // If comparison field is empty, skip validation
        }

        const currentDate = moment(value, 'DD/MM/YYYY', true);
        const compareDate = moment(compareValue, 'DD/MM/YYYY', true);

        if (!currentDate.isValid() || !compareDate.isValid()) {
          return this.createError({
            message: 'Invalid date format',
          });
        }

        // Validate: start date should not be greater than stop date
        // Validate: stop date should not be smaller than start date
        if (isStartDateField) {
          // This is start date - should not be after stop date
          if (currentDate.isAfter(compareDate, 'day')) {
            return this.createError({
              message: 'Start date must not be greater than stop date',
            });
          }
        } else {
          // This is stop date - should not be before start date
          if (currentDate.isBefore(compareDate, 'day')) {
            return this.createError({
              message: 'Stop date must not be smaller than start date',
            });
          }
        }

        return true;
      });
    }

    // Custom validation for RADIO_BUTTONS with dependent fields
    if (
      fe.type === 'RADIO_BUTTONS' &&
      Array.isArray(fe.dependFieldKeys) &&
      fe.dependFieldKeys.length > 0 &&
      fe.dependCategoryKey
    ) {
      validator = validator.test(
        'dependent-fields-match',
        fe?.dependErrorText || 'Criteria does not met',
        function (value, context) {
          // Only enforce when user selects "Yes"
          const cond = fe?.dependErrorValue || 'Yes';
          if (value === cond) {
            // Use context.parent to access root form values, fallback to context.options.context for root-level fields
            const formValues = context.options.context || context.parent;
            const allMatch = fe.dependFieldKeys.every((dep) => {
              const dependsValue = formValues?.[fe.dependCategoryKey]?.[dep.key];
              return dependsValue === dep.value;
            });

            if (!allMatch) {
              return this.createError({
                message: fe?.dependErrorText,
              });
            }
          }
          return true;
        }
      );
    }
    const _isNewValidationEnabled = isNewValidationEnabled();
    // Async validations based on fieldKey (only if new validation system is enabled)
    // Note: fe.key is the unique field identifier (e.g., "ageCriteria", "dateOfDiagnosis")
    //       fe.fieldKey is the special validation type identifier (e.g., "ageCriteria", "dateOfDiagnosis")
    //       For some fields, fe.key IS the validation type (like "ageCriteria"), so we check both
    const validationKey = fe.fieldKey || fe.key; // Use fieldKey if present, otherwise use key
    
    if (_isNewValidationEnabled && validationKey) {
      
      const fieldConfig = fe.fieldConfig || fe;
      const switchKey = validationKey; // Use the determined validation key

      switch (switchKey) {
        case 'dateOfDiagnosis':
          validator = validator.test(
            'date-of-diagnosis-range',
            'Patient must be diagnosed at least 6 months and not more than 8 years before index date',
            async function (value, context) {
              if (!value) {
                return true; // Empty values handled by required validation
              }

              try {
                const isValid = await validateDateOfDiagnosis(
                  value,
                  fieldConfig,
                  context.options.context,
                  validationContext
                );

                if (isValid !== true) {
                  const errorMessage =
                    typeof isValid === 'object' && isValid?.message
                      ? isValid.message
                      : 'Patient must be diagnosed at least 6 months and not more than 8 years before index date';
                  const error = this.createError({ message: errorMessage });
                  return error;
                }
                return true;
              } catch (error) {
                console.error('[DynamicForm] Yup test: dateOfDiagnosis exception -', error);
                const errorObj = this.createError({ message: 'Unable to validate date of diagnosis' });
                console.debug('[DynamicForm] Yup test: dateOfDiagnosis error object created', errorObj);
                return errorObj;
              }
            }
          );
          break;
        
        case 'dateOfEnrolment':
          validator = validator.test(
            'date-of-enrolment-range',
            'Date of enrolment must be before index date',
            async function (value, context) {
              if (!value) {
                return true; // Empty values handled by required validation
              }

              try {
                const isValid = await validateDateOfEnrolment(
                  value,
                  fieldConfig,
                  context.options.context,
                  validationContext
                );

                if (isValid !== true) {
                  const errorMessage =
                    typeof isValid === 'object' && isValid?.message
                      ? isValid.message
                      : 'Date of enrolment must be before index date';
                  return this.createError({ message: errorMessage });
                }
                return true;
              } catch (error) {
                console.error('[DynamicForm] Yup test: dateOfEnrolment exception -', error);
                return this.createError({
                  message: 'Unable to validate date of enrolment',
                });
              }
            }
          );
          break; 
        
        case 'yearOfDiagnosis':
          validator = validator.test(
            'year-of-diagnosis-index-year',
            'Year of diagnosis cannot be greater than index date year',
            async function (value, context) {
              if (!value) {
                return true; // Empty values handled by required validation
              }
    
              try {
                const isValid = await validateYearOfDiagnosis(
                  value,
                  fieldConfig,
                  context.options.context,
                  validationContext,
                  validationContext.getSubjectAnswers
                );
    
                if (isValid !== true) {
                  const errorMessage =
                    typeof isValid === 'object' && isValid?.message
                      ? isValid.message
                      : 'Year of diagnosis cannot be greater than index date year';
                  return this.createError({ message: errorMessage });
                }
                return true;
              } catch (error) {
                console.error('[DynamicForm] Yup test: yearOfDiagnosis exception -', error);
                return this.createError({
                  message: 'Unable to validate year of diagnosis',
                });
              }
            }
          );
          break;
        case 'priorExposure':
          validator = validator.test(
            'prior-exposure-validation',
            'Prior exposure validation failed',
            async function (value, context) {
              if (!value) {
                return true; // Empty values handled by required validation
              }

              try {
                const isValid = await validatePriorExposure(
                  value,
                  fieldConfig,
                  context.options.context,
                  validationContext,
                  validationContext.getSubjectAnswers
                );

                if (isValid !== true) {
                  const errorMessage =
                    typeof isValid === 'object' && isValid?.message
                      ? isValid.message
                      : 'Prior exposure validation failed';
                  return this.createError({ message: errorMessage });
                }
                return true;
              } catch (error) {
                console.error('[DynamicForm] Yup test: priorExposure exception -', error);
                return this.createError({ message: 'Unable to validate prior exposure' });
              }
            }
          );
          break;

        case 'ageAtIndexDate':
          validator = validator.test(
            'age-at-index-date-range',
            'Age at Index Date should not be greater than age at date of enrolment. Please check',
            async function (value, context) {
              if (!value) {
                return true; // Empty values handled by required validation
              }

              try {
                const isValid = await validateAgeAtIndexDate(
                  value,
                  fieldConfig,
                  context.options.context,
                  validationContext
                );

                if (isValid !== true) {
                  const errorMessage =
                    typeof isValid === 'object' && isValid?.message
                      ? isValid.message
                      : 'Age at Index Date should not be greater than age at date of enrolment. Please check';
                  const error = this.createError({ message: errorMessage });
                  return error;
                }
                return true;
              } catch (error) {
                console.error('[DynamicForm] Yup test: ageAtIndexDate exception -', error);
                const errorObj = this.createError({ message: 'Unable to validate age at index date' });
                console.debug('[DynamicForm] Yup test: ageAtIndexDate error object created', errorObj);
                return errorObj;
              }
            }
          );
          break;

        case 'oesophagus_mostrecent_date':
        case 'stomach_mostrecent_date':
        case 'duodenum_mostrecent_date':
        case 'ileum_mostrecent_date':
        case 'jejunum_mostrecent_date':
        case 'cecum_mostrecent_date':
        case 'ascending_colon_mostrecent_date':
        case 'transverse_colon_mostrecent_date':
        case 'descending_colon_mostrecent_date':
        case 'sigmoid_colon_mostrecent_date':
        case 'rectum_mostrecent_date':
        case 'anus_mostrecent_date':
        case 'anastomosis_mostrecent_date':
        case 'inflammatory_mostrecent_date':
        case 'stenosing_stricturing_mostrecent_date':
        case 'penetrating_fistulizing_mostrecent_date':
        case 'perianal_disease_mostrecent_date':
          validator = validator.test(
            'most-recent-date-range',
            'Most recent CD characteristics date must be between diagnosis date and just before index date',
            async function (value, context) {
              if (!value) {
                return true; // Empty values handled by required validation
              }

              try {
                const isValid = await validateMostRecentDate(
                  value,
                  fieldConfig,
                  context.options.context,
                  validationContext,
                  validationContext.getSubjectAnswers
                );

                if (isValid !== true) {
                  const errorMessage =
                    typeof isValid === 'object' && isValid?.message
                      ? isValid.message
                      : 'Most recent CD characteristics date must be between diagnosis date and just before index date';
                  return this.createError({ message: errorMessage });
                }
                return true;
              } catch (error) {
                console.error('[DynamicForm] Yup test: mostRecentDate exception -', error);
                return this.createError({
                  message: 'Unable to validate most recent CD characteristics date',
                });
              }
            }
          );
          break;

        case 'upadacitinib_date_of_initiation':
        case 'risankizumab_date_of_initiation':
        case 'ustekinumab_date_of_initiation':
        case 'certolizumab_date_of_initiation':
        case 'adalimumab_date_of_initiation':
        case 'infliximab_date_of_initiation':

        case 'risankizumab_dose_administration_start_date':
        case 'ustekinumab_dose_administration_start_date':
        case 'certolizumab_dose_administration_start_date':
        case 'adalimumab_dose_administration_start_date':
        case 'upadacitinib_dose_administration_start_date':
        // case 'infliximab_dose_administration_start_date':

        case '5-asa_start_date':
        case 'other_immunomodulator_start_date':
        case 'Corticosteroids_start_date':
        case 'tacrolimus_start_date':
        case 'methotrexate_start_date':
        case 'mercaptopurine_start_date':
        case 'ciclosporin_start_date':
        case 'azathioprine_start_date':
          validator = validator.test(
            '5-asa-start-date-range',
            '5-ASA start date must be between (Index date – 2 years) and index date',
            async function (value, context) {
              if (!value) {
                return true; // Empty values handled by required validation
              }

              try {
                const isValid = await validate5AsaStartDate(
                  value,
                  fieldConfig,
                  context.options.context,
                  validationContext,
                  validationContext.getSubjectAnswers
                );

                if (isValid !== true) {
                  const errorMessage =
                    typeof isValid === 'object' && isValid?.message
                      ? isValid.message
                      : '5-ASA start date must be between (Index date – 2 years) and index date';
                  return this.createError({ message: errorMessage });
                }
                return true;
              } catch (error) {
                console.error('[DynamicForm] Yup test: 5-asa_start_date exception -', error);
                return this.createError({
                  message: 'Unable to validate 5-ASA start date',
                });
              }
            }
          );
          break;

        case '5-asa_end_date':
        case 'azathioprine_end_date':
        case 'ciclosporin_end_date':
        case 'mercaptopurine_end_date':
        case 'methotrexate_end_date':
        case 'tacrolimus_end_date':
        case 'other_immunomodulator_end_date':
        case 'corticosteroids_end_date':
          
        case 'infliximab_date_of_discontinuation':
        case 'adalimumab_date_of_discontinuation':
        case 'certolizumab_date_of_discontinuation':
        case 'ustekinumab_date_of_discontinuation':
        case 'risankizumab_date_of_discontinuation':
        case 'upadacitinib_date_of_discontinuation':
          validator = validator.test(
            '5-asa-end-date-range',
            'End date validation failed',
            async function (value, context) {
              if (!value) {
                return true; // Empty values handled by required validation
              }

              try {
                const isValid = await validate5AEndDate(
                  value,
                  fieldConfig,
                  context.options.context,
                  validationContext,
                  validationContext.getSubjectAnswers
                );

                if (isValid !== true) {
                  const errorMessage =
                    typeof isValid === 'object' && isValid?.message ? isValid.message : 'End date validation failed';
                  return this.createError({ message: errorMessage });
                }
                return true;
              } catch (error) {
                console.error('[DynamicForm] Yup test: 5-asa_end_date exception -', error);
                return this.createError({
                  message: 'Unable to validate end date',
                });
              }
            }
          );
          break;
        
        case 'HBI_at_week_26_individual_scores_available__No__HBI_date_at_week_26':
        case 'HBI_at_week_26_individual_scores_available__Yes__HBI_date_at_week_26':
          validator = validator.test(
            'HBI-at-week-26-date-range',
            'HBI date at week 26 must be between index date and 26 weeks after index date',
            async function (value, context) {
              if (!value) {
                return true; 
              }
              try {
                const isValid = await validateDateAfter26WeeksIndex(
                  value,
                  fieldConfig,
                  context.options.context,
                  validationContext,
                  validationContext.getSubjectAnswers
                );
                if (isValid !== true) {
                  const errorMessage =
                    typeof isValid === 'object' && isValid?.message ? isValid.message : 'HBI date at week 26 must be between index date and 26 weeks after index date';
                  return this.createError({ message: errorMessage });
                }
                return true;
              } catch (error) {
                console.error('[DynamicForm] Yup test: HBI_at_week_26_available__No__HBI_at_week_26__VDZ_treatment_stopped exception -', error);
                return this.createError({
                  message: 'Unable to validate HBI at week 26 available',
                });
              }
            }
            );
        break;

        case 'HBI_at_baseline_individual_scores_available__Yes__HBI_date_at_initiation_of_VDZ_therapy':
        case 'HBI_at_baseline_individual_scores_available__No__HBI_date_at_initiation_of_VDZ_therapy':
          validator = validator.test(
            'date-within-6-months-of-index',
            'Date must be within ±6 months of index date',
            async function (value, context) {
              if (!value) {
                return true; // Empty values handled by required validation
              }

              try {
                const isValid = await validateDateWithin6MonthsOfIndex(
                  value,
                  fieldConfig,
                  context.options.context,
                  validationContext,
                  validationContext.getSubjectAnswers
                );

                if (isValid !== true) {
                  const errorMessage =
                    typeof isValid === 'object' && isValid?.message
                      ? isValid.message
                      : 'Date must be within ±6 months of index date';
                  return this.createError({ message: errorMessage });
                }
                return true;
              } catch (error) {
                console.error('[DynamicForm] Yup test: dateWithin6MonthsOfIndex exception -', error);
                return this.createError({
                  message: 'Unable to validate date within 6 months of index date',
                });
              }
            }
          );
          break;

        case 'dateRangeDiagnosisToDiagnosisPlus6Months':
          validator = validator.test(
            'date-range-diagnosis-to-diagnosis-plus-6-months',
            'Date must be between date of diagnosis and (date of diagnosis + 6 months)',
            async function (value, context) {
              if (!value) {
                return true; // Empty values handled by required validation
              }

              try {
                const isValid = await validateDateRangeDiagnosisToDiagnosisPlus6Months(
                  value,
                  fieldConfig,
                  context.options.context,
                  validationContext,
                  validationContext.getSubjectAnswers
                );

                if (isValid !== true) {
                  const errorMessage =
                    typeof isValid === 'object' && isValid?.message
                      ? isValid.message
                      : 'Date must be between date of diagnosis and (date of diagnosis + 6 months)';
                  return this.createError({ message: errorMessage });
                }
                return true;
              } catch (error) {
                console.error('[DynamicForm] Yup test: dateRangeDiagnosisToDiagnosisPlus6Months exception -', error);
                return this.createError({
                  message: 'Unable to validate date range',
                });
              }
            }
          );
          break;

        case 'dateRangeIndexMinus6Months':
          validator = validator.test(
            'date-range-index-minus-6-months',
            'Date must be between (Index date – 6 months) and index date',
            async function (value, context) {
              if (!value) {
                return true; // Empty values handled by required validation
              }

              try {
                const isValid = await validateDateRangeIndexMinus6Months(
                  value,
                  fieldConfig,
                  context.options.context,
                  validationContext,
                  validationContext.getSubjectAnswers
                );

                if (isValid !== true) {
                  const errorMessage =
                    typeof isValid === 'object' && isValid?.message
                      ? isValid.message
                      : 'Date must be between (Index date – 6 months) and index date';
                  return this.createError({ message: errorMessage });
                }
                return true;
              } catch (error) {
                console.error('[DynamicForm] Yup test: dateRangeIndexMinus6Months exception -', error);
                return this.createError({
                  message: 'Unable to validate date range',
                });
              }
            }
          );
          break;

        case 'dateAtWeeksAfterIndex':
          validator = validator.test(
            'date-at-weeks-after-index',
            'Date must be approximately at week 10, 14, 26, or 52 after index date',
            async function (value, context) {
              if (!value) {
                return true; // Empty values handled by required validation
              }

              try {
                const isValid = await validateDateAtWeeksAfterIndex(
                  value,
                  fieldConfig,
                  context.options.context,
                  validationContext,
                  validationContext.getSubjectAnswers
                );

                if (isValid !== true) {
                  const errorMessage =
                    typeof isValid === 'object' && isValid?.message
                      ? isValid.message
                      : 'Date must be approximately at week 10, 14, 26, or 52 after index date';
                  return this.createError({ message: errorMessage });
                }
                return true;
              } catch (error) {
                console.error('[DynamicForm] Yup test: dateAtWeeksAfterIndex exception -', error);
                return this.createError({
                  message: 'Unable to validate date at weeks after index date',
                });
              }
            }
          );
          break;

        case 'dateRangeIndexMinus12Months':
          validator = validator.test(
            'date-range-index-minus-12-months',
            'Date must be between (Index date – 12 months) and index date',
            async function (value, context) {
              if (!value) {
                return true; // Empty values handled by required validation
              }

              try {
                const isValid = await validateDateRangeIndexMinus12Months(
                  value,
                  fieldConfig,
                  context.options.context,
                  validationContext,
                  validationContext.getSubjectAnswers
                );

                if (isValid !== true) {
                  const errorMessage =
                    typeof isValid === 'object' && isValid?.message
                      ? isValid.message
                      : 'Date must be between (Index date – 12 months) and index date';
                  return this.createError({ message: errorMessage });
                }
                return true;
              } catch (error) {
                console.error('[DynamicForm] Yup test: dateRangeIndexMinus12Months exception -', error);
                return this.createError({
                  message: 'Unable to validate date range',
                });
              }
            }
          );
          break;

        case 'dateAfterIndexAndInitiation':
          validator = validator.test(
            'date-after-index-and-initiation',
            'Date must be after index date and date of initiation',
            async function (value, context) {
              if (!value) {
                return true; // Empty values handled by required validation
              }

              try {
                const isValid = await validateDateAfterIndexAndInitiation(
                  value,
                  fieldConfig,
                  context.options.context,
                  validationContext,
                  validationContext.getSubjectAnswers
                );

                if (isValid !== true) {
                  const errorMessage =
                    typeof isValid === 'object' && isValid?.message
                      ? isValid.message
                      : 'Date must be after index date and date of initiation';
                  return this.createError({ message: errorMessage });
                }
                return true;
              } catch (error) {
                console.error('[DynamicForm] Yup test: dateAfterIndexAndInitiation exception -', error);
                return this.createError({
                  message: 'Unable to validate date after index and initiation',
                });
              }
            }
          );
          break;

        case 'ageCriteria':
          validator = validator.test(
            'age-criteria-match',
            'Age criteria validation failed',
            async function (value, context) {
              if (!value) {
                return true; // Empty values handled by required validation
              }

              try {
                const isValid = await validateAgeCriteria(
                  value,
                  fieldConfig,
                  context.options.context,
                  validationContext,
                  validationContext.getSubjectAnswers
                );

                if (isValid !== true) {
                  const errorMessage =
                    typeof isValid === 'object' && isValid?.message
                      ? isValid.message
                      : 'Age criteria validation failed';
                  return this.createError({ message: errorMessage });
                }
                return true;
              } catch (error) {
                console.error('[DynamicForm] Yup test: ageCriteria exception -', error);
                return this.createError({ message: 'Unable to validate age criterion' });
              }
            }
          );
          break;

        case 'diagnosedWithCDin6months':
          validator = validator.test(
            'diagnosed-with-cd-6months',
            'Diagnosis duration validation failed',
            async function (value, context) {
              if (!value) {
                return true; // Empty values handled by required validation
              }

              try {
                const isValid = await validateDiagnosedWithCDin6months(
                  value,
                  fieldConfig,
                  context.options.context,
                  validationContext,
                  validationContext.getSubjectAnswers
                );

                if (isValid !== true) {
                  const errorMessage =
                    typeof isValid === 'object' && isValid?.message
                      ? isValid.message
                      : 'Diagnosis duration validation failed';
                  return this.createError({ message: errorMessage });
                }
                return true;
              } catch (error) {
                console.error('[DynamicForm] Yup test: diagnosedWithCDin6months exception -', error);
                return this.createError({ message: 'Unable to validate age criterion' });
              }
            }
          );
          break;

        case 'eligibilityConfirmation':
          validator = validator.test(
            'eligibility-confirmation',
            'Eligibility confirmation validation failed',
            async function (value, context) {
              if (!value) {
                return true; // Empty values handled by required validation
              }

              try {
                const isValid = await validateEligibilityConfirmation(
                  value,
                  fieldConfig,
                  context.options.context,
                  validationContext,
                  validationContext.getSubjectAnswers
                );

                if (isValid !== true) {
                  const errorMessage =
                    typeof isValid === 'object' && isValid?.message
                      ? isValid.message
                      : 'Eligibility confirmation validation failed';
                  return this.createError({ message: errorMessage });
                }
                return true;
              } catch (error) {
                console.error('[DynamicForm] Yup test: eligibilityConfirmation exception -', error);
                return this.createError({ message: 'Unable to validate eligibility confirmation' });
              }
            }
          );
          break;

        case 'severityHbi':
          validator = validator.test('severity-hbi', 'HBI severity validation failed', async function (value, context) {
            if (!value) {
              return true; // Empty values handled by required validation
            }

            try {
              const isValid = await validateSeverityHbi(
                value,
                fieldConfig,
                context.options.context,
                validationContext,
                validationContext.getSubjectAnswers
              );

              if (isValid !== true) {
                const errorMessage =
                  typeof isValid === 'object' && isValid?.message ? isValid.message : 'HBI severity validation failed';
                return this.createError({ message: errorMessage });
              }
              return true;
            } catch (error) {
              console.error('[DynamicForm] Yup test: severityHbi exception -', error);
              return this.createError({ message: 'Unable to validate HBI severity' });
            }
          });
          break;

        case 'hbiAssessmentConfirmation':
          validator = validator.test(
            'hbi-assessment-confirmation',
            'HBI assessment confirmation validation failed',
            async function (value, context) {
              if (!value) {
                return true; // Empty values handled by required validation
              }

              try {
                const isValid = await validateHbiAssessmentConfirmation(
                  value,
                  fieldConfig,
                  context.options.context,
                  validationContext,
                  validationContext.getSubjectAnswers
                );

                if (isValid !== true) {
                  const errorMessage =
                    typeof isValid === 'object' && isValid?.message
                      ? isValid.message
                      : 'HBI assessment confirmation validation failed';
                  return this.createError({ message: errorMessage });
                }
                return true;
              } catch (error) {
                console.error('[DynamicForm] Yup test: hbiAssessmentConfirmation exception -', error);
                return this.createError({ message: 'Unable to validate HBI assessment confirmation' });
              }
            }
          );
          break;

        case 'patientsWithNoCDSTScore':
          validator = validator.test(
            'patients-with-no-cdst-score',
            'CDST score validation failed',
            async function (value, context) {
              if (!value) {
                return true;
              }

              try {
                const isValid = await validatePatientsWithNoCDSTScore(
                  value,
                  fieldConfig,
                  context.options.context,
                  validationContext,
                  validationContext.getSubjectAnswers
                );

                if (isValid !== true) {
                  const errorMessage =
                    typeof isValid === 'object' && isValid?.message ? isValid.message : 'CDST score validation failed';
                  return this.createError({ message: errorMessage });
                }
                return true;
              } catch (error) {
                console.error('[DynamicForm] Yup test: patientsWithNoCDSTScore exception -', error);
                return this.createError({ message: 'Unable to validate CDST score' });
              }
            }
          );
          break;

        case 'durationDisease':
          validator = validator.test(
            'duration-disease',
            'Duration of disease validation failed',
            async function (value, context) {
              if (!value) {
                return true; // Empty values handled by required validation
              }

              try {
                const isValid = await validateDurationDisease(
                  value,
                  fieldConfig,
                  context.options.context,
                  validationContext,
                  validationContext.getSubjectAnswers
                );

                if (isValid !== true) {
                  const errorMessage =
                    typeof isValid === 'object' && isValid?.message
                      ? isValid.message
                      : 'Duration of disease validation failed';
                  return this.createError({ message: errorMessage });
                }
                return true;
              } catch (error) {
                console.error('[DynamicForm] Yup test: durationDisease exception -', error);
                return this.createError({ message: 'Unable to validate duration of disease' });
              }
            }
          );
          break;

        case 'index_date_between_jan_2016_and_feb_2024':
          validator = validator.test(
            'index-date-range',
            'Index date range validation failed',
            async function (value, context) {
              if (!value) {
                return true; // Empty values handled by required validation
              }

              try {
                const isValid = await validateIndexDateBetweenJan2016AndFeb2024(
                  value,
                  fieldConfig,
                  context.options.context,
                  validationContext,
                  validationContext.getSubjectAnswers
                );

                if (isValid !== true) {
                  const errorMessage =
                    typeof isValid === 'object' && isValid?.message
                      ? isValid.message
                      : 'Index date range validation failed';
                  return this.createError({ message: errorMessage });
                }
                return true;
              } catch (error) {
                console.error('[DynamicForm] Yup test: index_date_between_jan_2016_and_feb_2024 exception -', error);
                return this.createError({ message: 'Unable to validate index date' });
              }
            }
          );
          break;

        default:
          break;
      }
    }

    fieldValidationSchema[fe.key] = validator;

  } else if (fe.type === "CHECK_BOX") {
    if (fe.valueRequired) {
      fieldValidationSchema[fe.key] = yup.boolean().oneOf([true], 'This field is required');
    } else {
      fieldValidationSchema[fe.key] = yup.boolean();
    }
  } else if (fe.type === "FILE_UPLOAD") {
    if (fe.required) {
      fieldValidationSchema[fe.key] = yup
        .string()
        .required("A file is required");
    }
    fieldValidationSchema[fe.key + "_inputFile"] = yup.mixed();
  } else if (fe.type === "MULTISELECT_DROP_DOWN") {
    if (fe.required) {
      fieldValidationSchema[fe.key] = yup.array().of(yup.string()).min(1, 'At least one selection is required');
    } else {
      fieldValidationSchema[fe.key] = yup.array().of(yup.string());
    }
  } else if (fe.type === "SEARCH_DROP_DOWN") {
    if (fe.required) {
      fieldValidationSchema[fe.key] = yup.array().of(yup.string()).min(1, "At least one selection is required");
    } else {
      fieldValidationSchema[fe.key] = yup.array().of(yup.string());
    }
  } else if(fe.type === "PHONE_INPUT"){
    if (fe.required) {
      fieldValidationSchema[fe.key] = phoneValidationSchema.concat(yup.string().required('This field is required'));
    } else {
      fieldValidationSchema[fe.key] = phoneValidationSchema;
    }
  }
  if (fe.fields && fe.fields.length > 0) {
    fe.fields.forEach((ch) => {
      getFieldValidationSchema(fieldValidationSchema, ch, validationContext);
    });
  }
};

const getValidationSchema = (_config, userRole, validationContext = null) => {
  console.debug('[DynamicForm] getValidationSchema: Building schema', {
    hasConfig: !!_config,
    userRole,
    hasValidationContext: !!validationContext,
    hasGetSubjectAnswers: !!validationContext?.getSubjectAnswers,
  });
  
  const _schema = {};
  const fieldsWithAsyncValidation = []; // Track fields with async validation
  
  _config.categories &&
    _config.categories.forEach((ct) => {
      // Skip UI alignment categories: testSpace and divider - these should never be in schema
      if (ct.key === "divider" || ct.key === "testSpace") {
        return; // Skip these UI-only categories completely
      }
      
      if (!ct.access || (ct.access && (ct.access == userRole || ct.access.includes(userRole)))) {
        const fieldValidationSchema = {};
        ct.fields &&
          ct.fields.forEach((fe) => {
            if (!fe.access || (fe.access && (fe.access == userRole || fe.access.includes(userRole)))) {
              // Check if this field will get async validation
              const validationKey = fe.fieldKey || fe.key;
              const asyncValidationFields = [
                'ageCriteria',
                'diagnosedWithCDin6months',
                'eligibilityConfirmation',
                'severityHbi',
                'hbiAssessmentConfirmation',
                'patientsWithNoCDSTScore',
                'durationDisease',
                'index_date_between_jan_2016_and_feb_2024',
                'priorExposure'
              ];
              if (isNewValidationEnabled() && validationKey && asyncValidationFields.includes(validationKey)) {
                fieldsWithAsyncValidation.push({
                  category: ct.key,
                  field: fe.key,
                  validationKey,
                  fieldType: fe.type,
                });
              }
              getFieldValidationSchema(fieldValidationSchema, fe, validationContext);
            }
          });
        _schema[ct.key] = yup.object().shape(fieldValidationSchema);
      }
    });
  _config.fields &&
    _config.fields.forEach((fe) => {
      if (!fe.access || (fe.access && (fe.access == userRole || fe.access.includes(userRole)))) {
        // Check if this field will get async validation
        const validationKey = fe.fieldKey || fe.key;
              const asyncValidationFields = [
                'ageCriteria',
                'diagnosedWithCDin6months',
                'eligibilityConfirmation',
                'severityHbi',
                'hbiAssessmentConfirmation',
                'patientsWithNoCDSTScore',
                'durationDisease',
                'index_date_between_jan_2016_and_feb_2024',
                'priorExposure'
              ];
        if (isNewValidationEnabled() && validationKey && asyncValidationFields.includes(validationKey)) {
          fieldsWithAsyncValidation.push({
            category: 'root',
            field: fe.key,
            validationKey,
            fieldType: fe.type,
          });
        }
        getFieldValidationSchema(_schema, fe, validationContext);
      }
    });
  
  // Debug: Log summary of schema
  console.debug('[DynamicForm] getValidationSchema: Schema built', {
    schemaKeys: Object.keys(_schema),
    fieldsWithAsyncValidation,
    totalAsyncValidations: fieldsWithAsyncValidation.length,
  });
  
  return _schema;
};

const getFieldInitValue = (_initVal, fe, formAnswers) => {
  const skipFields = ['LABEL', 'DATA_LABEL', 'BUTTON', 'DATA_HTML', 'REMOVE_FIELD_BUTTON', 'DOCUMENT_LINK', 'VIDEO_LINK','EMBED_VIDEO'];
  
  // Skip UI alignment fields: testSpace and divider (by key or type) - these should never be in initial values
  if (fe.key === "divider" || fe.key === "testSpace" || fe.type === "divider" || fe.type === "testSpace") {
    return; // Skip these UI-only fields completely
  }
  
  // Check if field has nested fields and if any of them are not in skipFields
  const hasValidNestedFields = fe.fields && fe.fields.length > 0 && 
    fe.fields.some(nestedField => !skipFields.includes(nestedField.type));
  
  const displayOnlyContainerTypes = ['LABEL', 'DATA_LABEL'];
  // LABEL/DATA_LABEL containers may wrap nested inputs; set initial values for nested fields only
  const shouldProcessAsNormalField = !_.includes(skipFields, fe.type) || hasValidNestedFields;
  const shouldSetFieldValue =
    shouldProcessAsNormalField && !displayOnlyContainerTypes.includes(fe.type);

  if (shouldSetFieldValue) {
    if (fe.type == "CHECK_BOX_GROUP") {
      _initVal[fe.key] = [];
      fe.options.values.forEach((fe1) => {
        if (
          formAnswers &&
          formAnswers[fe.key] &&
          formAnswers[fe.key].includes(fe1)
        ) {
          _initVal[fe.key].push(fe1);
        }
      });
    } else if (fe.type == "CHECK_BOX") {
      _initVal[fe.key] = formAnswers && formAnswers[fe.key] && formAnswers[fe.key] == "1" ? true : false;
    } else if (fe.type == "INLINE_TEXT_FIELD" && fe.subtype == "DATE") {
      _initVal[fe.key] =
        formAnswers && formAnswers[fe.key]
          ? formAnswers[fe.key]
          : fe.currentDate
            ? moment().format("DD/MM/YYYY")
            : '';
    } else if (fe.type == "YEAR_PICKER") {
      _initVal[fe.key] =
        formAnswers && formAnswers[fe.key] ? formAnswers[fe.key] : '';
    } else if (fe.type == "SLIDER") {
      _initVal[fe.key] =
        formAnswers && formAnswers[fe.key]
          ? Number(formAnswers[fe.key])
          : Number(fe.defaultValue);
    } else if (fe.type == "SIGNATURE") {
      _initVal[fe.key] =
        formAnswers && formAnswers[fe.key] ? formAnswers[fe.key] : "";
      // Only set signature date from existing answers - don't pre-populate with current time
      // The signature date will be set when the user actually saves their signature
      if (fe.currentDateKey) {
        _initVal[fe.currentDateKey] = formAnswers && formAnswers[fe.currentDateKey] ? formAnswers[fe.currentDateKey] : "";
      } else {
        _initVal[`${fe.key}_sign_date`] = formAnswers && formAnswers[`${fe.key}_sign_date`] ? formAnswers[`${fe.key}_sign_date`] : "";
      }
      // Signature mode (draw/type/upload) - only from existing answers
      _initVal[`${fe.key}_sign_mode`] = formAnswers && formAnswers[`${fe.key}_sign_mode`] ? formAnswers[`${fe.key}_sign_mode`] : "";
      // Signer name - only from existing answers
      _initVal[`${fe.key}_signer_name`] = formAnswers && formAnswers[`${fe.key}_signer_name`] ? formAnswers[`${fe.key}_signer_name`] : "";
    } else if (fe.type == "AUDIO") {
      _initVal[fe.key] =
        formAnswers && formAnswers[fe.key] ? formAnswers[fe.key] : false;
    } else if (fe.type == "MULTISELECT_DROP_DOWN" || fe.type === "SEARCH_DROP_DOWN") {
      _initVal[fe.key] = formAnswers && formAnswers[fe.key] ? Array.isArray(formAnswers[fe.key]) ? formAnswers[fe.key] : [formAnswers[fe.key]] : [];
    } else {
      _initVal[fe.key] =
        formAnswers && formAnswers[fe.key] ? formAnswers[fe.key] : "";
    }
    if (fe.showDate) {
      _initVal[fe.currentDateKey] = formAnswers && formAnswers[fe.currentDateKey] ? formAnswers[fe.currentDateKey] : moment().format("DD/MM/YYYY HH:mm:ss");
    }
  }
  fe.fields &&
    fe.fields.forEach((ch) => {
      getFieldInitValue(_initVal, ch, formAnswers);
    });
};

const getInitialVal = (_config, formAnswers, userRole) => {
  const _initVal = {};
  
  // Helper to check if user has access to a field
  const hasAccess = (field) => !field.access || (field.access && (field.access == userRole || field.access.includes(userRole)));
  
  // Helper to determine if field's initial value should be populated
  // Signature fields always get populated (even if user doesn't have access, to show disabled state with existing signature)
  const shouldPopulateField = (field) => field.type === 'SIGNATURE' || hasAccess(field);
  
  _config.categories &&
    _config.categories.forEach((ct) => {
      // Skip UI alignment categories: testSpace and divider - these should never be in initial values
      if (ct.key === "divider" || ct.key === "testSpace") {
        return; // Skip these UI-only categories completely
      }
      
      if (!ct.access || (ct.access && (ct.access == userRole || ct.access.includes(userRole)))) {
        _initVal[ct.key] = {};
        ct.fields &&
          ct.fields.forEach((fe) => {
            if (shouldPopulateField(fe)) {
              getFieldInitValue(
                _initVal[ct.key],
                fe,
                formAnswers && formAnswers[ct.key]
              );
            }
          });
      }
    });
  _config.fields &&
    _config.fields.forEach((fe) => {
      if (shouldPopulateField(fe)) {
        getFieldInitValue(_initVal, fe, formAnswers);
      }
    });
  return _initVal;
};

/**
 * Recursively collect all field keys from a field object (including nested fields)
 * @param {Object} field - The field object to collect keys from
 * @param {Array} keys - Array to store collected keys (passed by reference)
 * @param {string} categoryKey - Optional category key for nested fields
 */
const collectFieldKeys = (field, keys = [], categoryKey = null) => {
  const skipFields = ['LABEL', 'DATA_LABEL', 'BUTTON', 'DATA_HTML', 'REMOVE_FIELD_BUTTON', 'DOCUMENT_LINK', 'VIDEO_LINK', 'EMBED_VIDEO'];
  
  // Skip UI-only fields
  if (field.key === "divider" || field.key === "testSpace" || field.type === "divider" || field.type === "testSpace") {
    return;
  }
  
  // If field has a key and is not a skip field, add it
  if (field.key && !skipFields.includes(field.type)) {
    if (categoryKey) {
      keys.push({ key: field.key, categoryKey });
    } else {
      keys.push({ key: field.key, categoryKey: null });
    }
    
    // Handle special field types that have additional keys
    if (field.type === "SIGNATURE") {
      if (field.currentDateKey) {
        if (categoryKey) {
          keys.push({ key: field.currentDateKey, categoryKey });
        } else {
          keys.push({ key: field.currentDateKey, categoryKey: null });
        }
      } else {
        const signDateKey = `${field.key}_sign_date`;
        if (categoryKey) {
          keys.push({ key: signDateKey, categoryKey });
        } else {
          keys.push({ key: signDateKey, categoryKey: null });
        }
      }
    }
    
    if (field.showDate && field.currentDateKey) {
      if (categoryKey) {
        keys.push({ key: field.currentDateKey, categoryKey });
      } else {
        keys.push({ key: field.currentDateKey, categoryKey: null });
      }
    }
    
    if (field.type === "FILE_UPLOAD") {
      const inputFileKey = `${field.key}_inputFile`;
      if (categoryKey) {
        keys.push({ key: inputFileKey, categoryKey });
      } else {
        keys.push({ key: inputFileKey, categoryKey: null });
      }
    }
  }
  
  // Recursively process nested fields
  if (field.fields && Array.isArray(field.fields)) {
    field.fields.forEach((nestedField) => {
      collectFieldKeys(nestedField, keys, categoryKey);
    });
  }
};

const DynamicForm = (props) => {
  const {
    _config,
    sectionKey,
    subSectionKey,
    AddNewField,
    handleSave,
    readOnly,
    stepStatusData,
    showSkeleton = false,
    buttonLabel,
    showButton,
    navigateToNextTab,
    userSiteTrialRole,
    onFormChanged,
    handleSaveSignature,
    handleUploadFile,
    handleDownloadFile,
    handleDownloadSignature,
    formAnswers,
    sectionsLength,
    sectionIndex,
    removeCustomField,
    dynamicFormAction,
    setDynamicFormAction,
    queriesByFieldKey,
    hasOpenQueries,
    formConfig,
  } = props;
  const [validationSchema, setvalidationSchema] = useState(null);
  const [initialValuess, setInitialValuess] = useState(null);
  const [backupValues, setBackupValues] = useState(null);
  const [backupErrors, setBackupErrors] = useState(null);
  const formikHelpersRef = useRef(null);
  const [openOTP, setOpenOTP] = useState(false);
  const [generateOTPValue] = useGenerateOTPValueMutation();
  const [verifyOTPValue] = useVerifyOTPValueMutation();
  const { isSmallScreen } = useResponsive();
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    buttonLabel: "",
    message: "",
  });
  // State for field-specific confirmation validation dialog
  const [confirmationValidationDialog, setConfirmationValidationDialog] = useState({
    open: false,
    buttonLabel: "",
    cancelButtonLabel: "",
    title: "",
    message: "",
    triggeredRules: [],
  });
  // State for balancing criteria exceeded dialog
  const [balancingCriteriaDialog, setBalancingCriteriaDialog] = useState({
    open: false,
    buttonLabel: "",
    cancelButtonLabel: "",
    title: "",
    message: "",
    exceededCriteria: [],
    saveAsOnHold: false,
  });
  // State for dependsOn validation dialog (hidden/disabled fields with values)
  const [dependsOnValidationDialog, setDependsOnValidationDialog] = useState({
    open: false,
    problematicFields: [],
  });
  const userDetails = useSelector((state) => state.userDetails);
  const dispatch = useDispatch()
  const { trialId, trialSiteId, siteInitStep, subjectMasterId, crfMasterId } = useParams();
  const sponsorId = useSelector((state) => state.auth.sponsorId);
  const trialSite = useSelector((state) => state.trial?.trialSiteDetail);
  const [getSubjectAnswers] = bloqcibeApi.endpoints.getSubjectAnswers.useLazyQuery();
  const [getVisitAnswers] = bloqcibeApi.endpoints.getVisitAnswers.useLazyQuery();
  const [getSubjectCountByField] = bloqcibeApi.endpoints.getSubjectCountByField.useLazyQuery();

  const currentFormStatus = useMemo(() => {
    if (!stepStatusData) return null;
    let status = null;

    if (sectionKey) {
      const section = stepStatusData?.sectionStatuses?.find((section) => section.sectionKey === sectionKey);
      if (section) {
        status = section.status;
      }
      if (subSectionKey) {
        const subSection = section?.subSectionStatuses?.find((subSection) => subSection.subSectionKey === subSectionKey);
        if (subSection) {
          status = subSection.status;
        }
      }
    }
    return status;
  }, [stepStatusData, sectionKey, subSectionKey]);

  // Create validation context for async validations
  const validationContext = useMemo(() => {
    const siteId = trialSite?.siteTrialData?.site?.id;
    if (!siteId || !sponsorId || !trialId) {
      return null;
    }

    return {
      sponsorId,
      trialId,
      siteId,
      subjectMasterId,
      crfMasterId,
      userSiteTrialRole,
      getSubjectAnswers,
      getVisitAnswers,
      getSubjectCountByField,
      trialSite,
      currentFormStatus,
    };
  }, [
    sponsorId,
    trialId,
    trialSite,
    subjectMasterId,
    crfMasterId,
    userSiteTrialRole,
    getSubjectAnswers,
    getVisitAnswers,
    getSubjectCountByField,
    currentFormStatus,
  ]);

  useEffect(() => {
    if (userSiteTrialRole && _config && !backupValues) {
      const initialValues = getInitialVal(_config, formAnswers, userSiteTrialRole);
      // console.log('Initial Values', initialValues);
      setInitialValuess(initialValues);
    }
  }, [userSiteTrialRole, formAnswers, _config, backupValues]);
  useEffect(() => {
    if (userDetails && (!userDetails.locationLat || !userDetails.locationLng)) {
      getGeoLocation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDetails])

  useEffect(() => {
    if (_config) {
      onFormChanged && onFormChanged(sectionKey, subSectionKey);
      const yepSchema = getValidationSchema(_config, userSiteTrialRole, validationContext);
      setvalidationSchema(yup.object().shape(yepSchema));
      if (backupValues) {
        const initialValues = getInitialVal(_config, formAnswers, userSiteTrialRole)

        // Merge backup values with new initial values to preserve user input
        const mergedValues = _.merge({}, initialValues, backupValues);

        setInitialValuess(mergedValues);
      }
    }
  }, [_config, validationContext, userSiteTrialRole]);

  // Restore errors when backupErrors exists and formikHelpers is available
  useEffect(() => {
    if (backupErrors && formikHelpersRef.current) {
      const currentErrors = formikHelpersRef.current.errors || {};
      const currentErrorsStr = JSON.stringify(currentErrors);
      const backupErrorsStr = JSON.stringify(backupErrors);
      
      // Only restore if errors are different to avoid infinite loops
      if (currentErrorsStr !== backupErrorsStr) {
        formikHelpersRef.current.setErrors(backupErrors);
        
        // Mark all error fields as touched so errors are visible on the fields
        const markFieldsTouched = (errors, prefix = '') => {
          Object.keys(errors).forEach((key) => {
            const fullPath = prefix ? `${prefix}.${key}` : key;
            if (typeof errors[key] === 'object' && errors[key] !== null && !Array.isArray(errors[key])) {
              // Nested error (category.field structure)
              markFieldsTouched(errors[key], fullPath);
            } else {
              // Leaf error - mark field as touched so error is visible on the field component
              formikHelpersRef.current.setFieldTouched(fullPath, true);
            }
          });
        };
        markFieldsTouched(backupErrors);
      }
    }
  }, [backupErrors, initialValuess]);

  useEffect(() => {
    if (dynamicFormAction) {
      // Support both string action (legacy) and object action { type, message }
      const actionType = typeof dynamicFormAction === 'object' ? dynamicFormAction.type : dynamicFormAction;
      const actionMessage = typeof dynamicFormAction === 'object' ? dynamicFormAction.message : null;
      
      switch (actionType) {
        case DynamicFormAction.FORM_SAVE_SUCCESS:
          setOpenOTP(false);
          setBackupValues(null);
          setBackupErrors(null);
          dispatch(
            openMessage({
              // Use API response message if present, otherwise use default success message
              message: actionMessage || 'Submitted Successfully!',
              messageSeverity: 'success',
            })
          );
          dispatch(openBackdropLoading({ backdropLoading: false }));
          navigateToNextTab && navigateToNextTab();
          setDynamicFormAction && setDynamicFormAction(DynamicFormAction.NA);
          break;
        case DynamicFormAction.FORM_SAVE_FAILURE:
          setOpenOTP(false);

          if (backupValues) {
            setInitialValuess(backupValues);
          }
          dispatch(bloqcibeApi.util.invalidateTags(['SubjectList', 'SubjectWithdrawList', 'OnHoldSubjectList']));
          dispatch(
            openMessage({
              message: actionMessage || 'Failed to submit form!',
              messageSeverity: 'error',
            })
          );
          dispatch(openBackdropLoading({ backdropLoading: false }));
          setDynamicFormAction && setDynamicFormAction(DynamicFormAction.NA);
          break;
        case DynamicFormAction.FORM_SAVE_WARNING:
          setOpenOTP(false);

          if (backupValues) {
            setInitialValuess(backupValues);
          }
          dispatch(bloqcibeApi.util.invalidateTags(['SubjectList', 'SubjectWithdrawList', 'OnHoldSubjectList']));
          dispatch(
            openMessage({
              message: actionMessage || 'Subject has been reserved for balancing!',
              messageSeverity: 'warning',
            })
          );
          dispatch(openBackdropLoading({ backdropLoading: false }));
          setDynamicFormAction && setDynamicFormAction(DynamicFormAction.NA);
          break;
        default:
          // console.log('Form Filled');
          break;
      }
    };
  }, [dynamicFormAction]);

  const handleAddNewField = async (parentKey, keys, values, errors) => {
    setBackupValues(values)
    if (errors) {
      setBackupErrors(errors)
    }
    // Await the AddNewField if it returns a Promise
    await AddNewField(parentKey, keys)
  }

  // Wrapper for removeCustomField that also deletes answers and errors
  const handleRemoveCustomField = (field, keys) => {
    if (!removeCustomField) {
      return;
    }

    // Collect all field keys that need to be deleted (including nested fields)
    const fieldKeysToDelete = [];
    const categoryKey = keys?.categoryKey;
    collectFieldKeys(field, fieldKeysToDelete, categoryKey);

    // Delete values and errors using formikHelpers if available
    if (formikHelpersRef.current) {
      const { setFieldValue, setErrors, setFieldTouched, errors: currentErrors } = formikHelpersRef.current;
      
      // Delete field values and clear touched state
      fieldKeysToDelete.forEach(({ key, categoryKey: catKey }) => {
        if (catKey) {
          // Nested field (category.field)
          const fieldPath = `${catKey}.${key}`;
          setFieldValue(fieldPath, undefined);
          setFieldTouched(fieldPath, false);
        } else {
          // Root-level field
          setFieldValue(key, undefined);
          setFieldTouched(key, false);
        }
      });

      // Delete field errors
      if (currentErrors) {
        const updatedErrors = { ...currentErrors };
        let hasChanges = false;

        fieldKeysToDelete.forEach(({ key, categoryKey: catKey }) => {
          if (catKey) {
            // Nested error (category.field)
            if (updatedErrors[catKey] && updatedErrors[catKey][key] !== undefined) {
              delete updatedErrors[catKey][key];
              hasChanges = true;
              // Clean up empty category error objects
              if (Object.keys(updatedErrors[catKey]).length === 0) {
                delete updatedErrors[catKey];
              }
            }
          } else {
            // Root-level error
            if (updatedErrors[key] !== undefined) {
              delete updatedErrors[key];
              hasChanges = true;
            }
          }
        });

        if (hasChanges) {
          setErrors(updatedErrors);
        }
      }
    }

    // Also update initialValuess to remove the field values
    if (initialValuess) {
      const updatedInitialValues = _.cloneDeep(initialValuess);
      let hasChanges = false;

      fieldKeysToDelete.forEach(({ key, categoryKey: catKey }) => {
        if (catKey) {
          // Nested field (category.field)
          if (updatedInitialValues[catKey] && updatedInitialValues[catKey][key] !== undefined) {
            delete updatedInitialValues[catKey][key];
            hasChanges = true;
          }
        } else {
          // Root-level field
          if (updatedInitialValues[key] !== undefined) {
            delete updatedInitialValues[key];
            hasChanges = true;
          }
        }
      });

      if (hasChanges) {
        setInitialValuess(updatedInitialValues);
      }
    }

    // Call the original removeCustomField function
    removeCustomField(field, keys);
  }

  const getGeoLocation = async () => {
    return new Promise((resolve) => {
      if ('geolocation' in navigator) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            // Do something with the latitude and longitude
            if (latitude && longitude) {
              dispatch(setUserLocation({ locationLatitude: latitude, locationLongitude: longitude }))
              resolve({ locationLat: latitude, locationLng: longitude });
            } else {
              resolve({ locationLat: null, locationLng: null });
            }
          },
          (error) => {
            console.error(`Error getting geolocation: ${error.message}`);
            // User denied location or location unavailable - continue with null values
            resolve({ locationLat: null, locationLng: null });
          }
        );
      } else {
        // Geolocation is not supported
        resolve({ locationLat: null, locationLng: null });
      }
    });
  }

  const _handleSubmit = async (values) => {
    let _locationObj = { ...userDetails };
    if (!_locationObj.locationLat || !_locationObj.locationLng) {
      _locationObj = await getGeoLocation();
      await processSubmit(values, _locationObj);
    } else {
      await processSubmit(values, _locationObj);
    }
  };

    const processSubmit = async (values, _locationObj) => {
      try {
        const currentTime = new Date();
        const changes = compareObjects(getInitialVal(_config, formAnswers, userSiteTrialRole), values);
        const payload = {
          stepKey: siteInitStep,
          sectionKey,
          subSectionKey,
          answers: {
            ...values,
            locationLatitude: _locationObj.locationLat,
            locationLongitude: _locationObj.locationLng,
            createdTime: currentTime,
            updating: Object.keys(changes).length > 0 ? true : false,
          },
        };
        await handleSave(payload);
        setConfirmDialog({ open: false, message: '', buttonLabel: '' });

        if (!dynamicFormAction) {
          setOpenOTP(false);
          setBackupValues(null);
          setBackupErrors(null);
          dispatch(
            openMessage({
              message: 'Submitted Successfully!',
              messageSeverity: 'success',
            })
          );
          navigateToNextTab && navigateToNextTab();
        }
      } catch (error) {
        console.error('Error in processSubmit:', error);
        setOpenOTP(false);
        setConfirmDialog({ open: false, message: '', buttonLabel: '' });

        dispatch(
          openMessage({
            message: 'Failed to submit form!',
            messageSeverity: 'error',
          })
        );
        dispatch(openBackdropLoading({ backdropLoading: false }));
      }
    };

  /**
   * Check balancing criteria and return exceeded criteria if any
   * Uses configuration from section-level balancingCriteria config
   * 
   * @param {Object} balancingConfig - The balancing criteria configuration
   * @param {Object} formValues - All form values
   * @returns {Promise<Object>} - { isExceeded, exceededCriteria, message }
   */
  /**
   * Check balancing criteria and return exceeded criteria if any
   * Uses configuration from section-level balancingCriteria config
   * 
   * CONSISTENCY NOTES (aligned with backend subject.service.ts):
   * - Uses Math.floor() for percentage to absolute number conversion
   * - Uses strict greater than (>) for comparison
   * - Handles non-percentage distribution as absolute number
   * - Default distribution is '%' if not specified
   * 
   * @param {Object} balancingConfig - The balancing criteria configuration
   * @param {Object} formValues - All form values
   * @returns {Promise<Object>} - { isExceeded, exceededCriteria, message }
   */
  const checkBalancingCriteria = async (balancingConfig, formValues) => {
    if (!balancingConfig?.enabled) {
      return { isExceeded: false, exceededCriteria: [], message: '' };
    }

    const siteId = trialSite?.siteTrialData?.site?.id;
    const totalSubjects = trialSite?.siteTrialData?.totalSubjects || 0;
    
    // Get site-specific config or default config
    const criteriaConfig = balancingConfig[siteId] || balancingConfig.default;
    
    if (!criteriaConfig) {
      return { isExceeded: false, exceededCriteria: [], message: '' };
    }

    // If total subjects is 0 or not set, we can't calculate thresholds
    if (!totalSubjects || totalSubjects <= 0) {
      console.warn('[DynamicForm] checkBalancingCriteria: Total subjects is 0 or not set, skipping check');
      return { isExceeded: false, exceededCriteria: [], message: '' };
    }

    const exceededCriteria = [];

    const statusFilter = {
      excludeStatuses: [
        SUBJECT_STATUSES.SCREENING_FAILURE,
        SUBJECT_STATUSES.ENROLLMENT_FAILURE,
        SUBJECT_STATUSES.WITHDRAWAL,
        SUBJECT_STATUSES.ON_HOLD,
      ],
    }

    // Iterate through each criteria group
    for (const [criteriaKey, criteria] of Object.entries(criteriaConfig)) {
      if (!criteria.checkOnHold || !criteria.fields) continue;

      // Check each field in the criteria
      for (const fieldConfig of criteria.fields) {
        const { categoryKey, fieldKey, values: thresholds } = fieldConfig;
        
        // Get the current field value from form values
        const fieldValue = formValues?.[categoryKey]?.[fieldKey];
        
        if (!fieldValue) continue;

        // Find the threshold configuration for the selected value
        const thresholdConfig = thresholds?.find(t => t.option === fieldValue);
        
        if (!thresholdConfig || !thresholdConfig.checkOnHold) continue;

        try {
          // Fetch current subject counts from API
          const result = await getSubjectCountByField({
            sponsorId,
            trialId,
            siteId,
            body: {
              fields: [
                {
                  stepKey: criteria.triggerStepKey,
                  sectionKey: criteria.triggerSectionKey,
                  subSectionKey: '',
                  categoryKey: categoryKey,
                  fieldKey: fieldKey,
                },
              ],
              statusFilter: statusFilter,
            },
          }).unwrap();

          const counts = result?.[0]?.counts || [];
          const currentCount = counts.find(c => c.value === fieldValue)?.subjectCount || 0;
          
          // Get distribution type - default to '%' if not specified (consistent with backend)
          const distribution = thresholdConfig.distribution || thresholdConfig.disribution || '%';
          const thresholdValue = thresholdConfig.value;
          
          // Calculate threshold number based on distribution type
          // Uses Math.floor() for consistency with backend
          let thresholdNumber;
          if (distribution === '%') {
            // Percentage distribution: convert to absolute number
            thresholdNumber = Math.floor((thresholdValue / 100) * totalSubjects);
          } else {
            // Absolute number distribution: use value directly
            thresholdNumber = thresholdValue;
          }
          
          // After adding this subject, the count would be:
          const newCount = currentCount + 1;
          
          // Calculate percentages for display purposes
          const currentPercentage = totalSubjects > 0 ? (currentCount / totalSubjects) * 100 : 0;
          const newPercentage = totalSubjects > 0 ? (newCount / totalSubjects) * 100 : 0;
          
          // Check if adding this subject would exceed the threshold
          // Uses strict greater than (>) for consistency with backend
          if (thresholdNumber > 0 && newCount > thresholdNumber) {
            exceededCriteria.push({
              criteriaKey,
              criteriaLabel: criteria.label,
              fieldLabel: fieldConfig.label || fieldKey,
              fieldValue,
              currentCount,
              newCount,
              thresholdNumber,
              currentPercentage: currentPercentage.toFixed(1),
              newPercentage: newPercentage.toFixed(1),
              thresholdValue,
              distribution,
              message: criteria.onHoldMessage,
            });
          }
        } catch (error) {
          console.error('[DynamicForm] checkBalancingCriteria: Error fetching subject counts', {
            criteriaKey,
            fieldKey,
            error: error?.message,
          });
          // On API error, skip this criteria check (fail open)
        }
      }
    }

    if (exceededCriteria.length > 0) {
      // Build detailed message with both counts and percentages
      const criteriaMessages = exceededCriteria
        .map(c => {
          const limitText = c.distribution === '%' 
            ? `${c.thresholdNumber} subjects (${c.thresholdValue}% of ${totalSubjects})`
            : `${c.thresholdNumber} subjects`;
          return `• ${c.criteriaLabel}: ${c.fieldValue} - ${c.newCount} subjects exceeds limit of ${limitText}`;
        })
        .join('\n');
      
      return {
        isExceeded: true,
        exceededCriteria,
        message: `${balancingConfig.message}\n\n${criteriaMessages}`,
      };
    }

    return { isExceeded: false, exceededCriteria: [], message: '' };
  };

  const checkSectionIndex = async (values, formikHelpers) => {
    console.debug('[DynamicForm] checkSectionIndex called', {
      isNewValidationEnabled: isNewValidationEnabled(),
      hasValidationContext: !!validationContext,
      hasFormikHelpers: !!formikHelpers,
      valuesKeys: Object.keys(values || {})
    });

    // Validate dependsOn and disabledDepends constraints BEFORE other validations
    try {
      const dependsOnValidation = await validateDependsOnConstraints(
        _config,
        values,
        {
          userSiteTrialRole,
        }
      );

      // If validation fails, show detailed dialog and prevent submission
      // Note: We do NOT set errors here - errors are set when user clicks "Review" button
      // This prevents race conditions and provides a cleaner UX
      if (!dependsOnValidation.isValid) {
        console.error('[DynamicForm] checkSectionIndex: DependsOn validation failed', {
          errors: dependsOnValidation.errors,
          errorsCount: Object.keys(dependsOnValidation.errors).length,
          problematicFieldsCount: dependsOnValidation.problematicFields?.length || 0,
        });

        // Store values and show detailed dialog with problematic fields
        // Errors will be set on fields when user clicks "Review" button
        setBackupValues(values);
        setDependsOnValidationDialog({
          open: true,
          problematicFields: dependsOnValidation.problematicFields || [],
        });

        // Prevent submission
        return;
      }
    } catch (error) {
      console.error('[DynamicForm] checkSectionIndex: Exception during dependsOn validation', {
        error: error?.message,
        stack: error?.stack,
      });
      // On validation error, allow submission (fail open) - can be changed to fail closed
      // This prevents blocking users if there's a bug in validation logic
    }

    // Validate at-least-one-when rules (e.g. if Anti-TNFa = Yes, at least one dependent must be Yes)
    try {
      const atLeastOneResult = validateAtLeastOneWhen(_config, values, {
        userSiteTrialRole,
      });
      if (!atLeastOneResult.isValid) {
        const helpers = formikHelpers || formikHelpersRef.current;
        if (helpers?.setErrors) {
          const mergedErrors = mergeErrors(helpers.errors || {}, atLeastOneResult.errors);
          helpers.setErrors(mergedErrors);
          atLeastOneResult.failedRules.forEach(({ triggerPath }) => {
            if (helpers.setFieldTouched) {
              helpers.setFieldTouched(triggerPath, true, false);
            }
          });
        }
        return;
      }
    } catch (error) {
      console.error('[DynamicForm] checkSectionIndex: Exception during atLeastOne validation', {
        error: error?.message,
        stack: error?.stack,
      });
      // On validation error, allow submission (fail open)
    }

    // Check for confirmation validations (field-specific confirmations before save)
    try {
      const confirmationConfig = getEffectiveConfirmationConfig(_config);
      if (confirmationConfig?.enabled) {
        const confirmationResult = checkConfirmationValidations(confirmationConfig, values);
        
        if (confirmationResult.shouldConfirm) {
          console.debug('[DynamicForm] checkSectionIndex: Confirmation validation triggered', {
            triggeredRules: confirmationResult.triggeredRules,
            rulesCount: confirmationResult.triggeredRules.length,
          });
          
          // Store values and show confirmation dialog
          setBackupValues(values);
          setConfirmationValidationDialog({
            open: true,
            message: confirmationResult.message,
            buttonLabel: confirmationResult.buttonLabel,
            cancelButtonLabel: confirmationResult.cancelButtonLabel,
            title: confirmationResult.title,
            triggeredRules: confirmationResult.triggeredRules,
          });
          
          // Prevent immediate submission - wait for user confirmation
          return;
        }
      }
    } catch (error) {
      console.error('[DynamicForm] checkSectionIndex: Exception during confirmation validation', {
        error: error?.message,
        stack: error?.stack,
      });
      // On validation error, allow submission (fail open) - can be changed to fail closed
    }

    // Check for balancing criteria exceeded (only if not in verification pending status)
    try {
      if (
        _config?.balancingCriteria?.enabled &&
        !['Verification_Pending', 'External_Verification_Pending', 'Completed'].includes(currentFormStatus)
      ) {
        const balancingResult = await checkBalancingCriteria(_config.balancingCriteria, values);

        if (balancingResult.isExceeded) {
          console.debug('[DynamicForm] checkSectionIndex: Balancing criteria exceeded', {
            exceededCriteria: balancingResult.exceededCriteria,
            criteriaCount: balancingResult.exceededCriteria.length,
          });

          // Store values and show balancing criteria dialog
          setBackupValues(values);
          setBalancingCriteriaDialog({
            open: true,
            message: balancingResult.message,
            buttonLabel: _config.balancingCriteria.buttonLabel || 'Save to Holding Area',
            cancelButtonLabel: _config.balancingCriteria.cancelButtonLabel || 'Review',
            title: _config.balancingCriteria.title || 'Balancing Criteria Exceeded',
            exceededCriteria: balancingResult.exceededCriteria,
            saveAsOnHold: true,
          });

          // Prevent immediate submission - wait for user confirmation
          return;
        }
      }
    } catch (error) {
      console.error('[DynamicForm] checkSectionIndex: Exception during balancing criteria check', {
        error: error?.message,
        stack: error?.stack,
      });
      // On error, allow submission (fail open) - can be changed to fail closed
    }

    // Continue with normal submission flow if validation passes
    if (_config.otpRequired) {
      setBackupValues(values);
      generateOTP();
      setOpenOTP(true);
    } else if (sectionsLength === sectionIndex) {
      setBackupValues(values);
      setConfirmDialog({
        open: true,
        message: _config?.submitConfirmationMessage || 'Are you sure you want to submit the details?',
        buttonLabel: 'Submit',
      });
    } else {
      _handleSubmit(values);
    }
  };
  
  const verifyOTP = async (otpVal) => {
    const verifyOtpRes = await verifyOTPValue({ payload: { otp: otpVal } });
    if (verifyOtpRes.data) {
      dispatch(openMessage({ message: 'OTP verified sucessfully!', messageSeverity: 'success' }));
      if (sectionsLength === sectionIndex) {
        setConfirmDialog({
          open: true,
          message: _config?.submitConfirmationMessage || 'Are you sure you want to submit the details?',
          buttonLabel: 'Submit',
        });
      } else {
        _handleSubmit(backupValues);
      }
    } else if (verifyOtpRes.error) {
      dispatch(openMessage({ message: verifyOtpRes.error?.data?.message, messageSeverity: 'error' }));
    }
  }
  const generateOTP = async () => {
    setOpenOTP(true);
    await generateOTPValue();
    dispatch(openMessage({ message: "OTP sent to your registered mobile number.", messageSeverity: "success" }))
  }

  const handleConfirmDialogClose = () => {
    // Don't clear backup values when user cancels - preserve their input for retry
    setOpenOTP(false);
    setConfirmDialog({ open: false, message: "", buttonLabel: "" });
  };

  // Handler for confirmation validation dialog close (Review button)
  const handleConfirmationValidationDialogClose = () => {
    setConfirmationValidationDialog({ 
      open: false, 
      message: "", 
      buttonLabel: "", 
      cancelButtonLabel: "",
      title: "",
      triggeredRules: [] 
    });
    // Keep backup values so user can retry
  };

  // Handler for confirmation validation dialog confirm - proceed with submission
  const handleConfirmationValidationDialogConfirm = () => {
    setConfirmationValidationDialog({ 
      open: false, 
      message: "", 
      buttonLabel: "", 
      cancelButtonLabel: "",
      title: "",
      triggeredRules: [] 
    });
    
    // User has already confirmed through the validation dialog
    // Skip the regular confirmation dialog and proceed directly to submission
    if (_config.otpRequired) {
      generateOTP();
      setOpenOTP(true);
    } else {
      // Directly submit - no need for another confirmation dialog
      _handleSubmit(backupValues);
    }
  };

  // Handler for balancing criteria dialog close (Review button)
  const handleBalancingCriteriaDialogClose = () => {
    setBalancingCriteriaDialog({ 
      open: false, 
      message: "", 
      buttonLabel: "", 
      cancelButtonLabel: "",
      title: "",
      exceededCriteria: [],
      saveAsOnHold: false,
    });
    // Keep backup values so user can retry
  };

  // Handler for balancing criteria dialog confirm - proceed with submission as ON_HOLD
  const handleBalancingCriteriaDialogConfirm = () => {
    const exceededCriteria = balancingCriteriaDialog.exceededCriteria;
    
    setBalancingCriteriaDialog({ 
      open: false, 
      message: "", 
      buttonLabel: "", 
      cancelButtonLabel: "",
      title: "",
      exceededCriteria: [],
      saveAsOnHold: false,
    });
    
    // User has confirmed to save to holding area
    if (_config.otpRequired) {
      setBackupValues(backupValues);
      generateOTP();
      setOpenOTP(true);
    } else {
      // Directly submit with ON_HOLD flag
      _handleSubmit(backupValues);
    }
  };

  /**
   * Handler for dependsOn validation dialog close (Review button)
   * 
   * When the user clicks "Review", we:
   * 1. Close the dialog
   * 2. Set specific errors on both the problematic fields AND their controlling fields
   * 3. Mark fields as touched so errors are visible
   * 4. Focus on the first error field
   */
  const handleDependsOnValidationDialogClose = () => {
    const problematicFields = dependsOnValidationDialog.problematicFields || [];
    
    // Close the dialog first
    setDependsOnValidationDialog({ 
      open: false, 
      problematicFields: [],
    });

    // If no problematic fields or no formik helpers, nothing more to do
    if (problematicFields.length === 0 || !formikHelpersRef.current) {
      return;
    }

    const { setErrors, setFieldTouched, errors: currentErrors } = formikHelpersRef.current;
    
    // Build error object with specific messages for problematic fields AND controlling fields
    const newErrors = {};
    const fieldsToTouch = [];
    const controllingFieldErrors = new Map(); // Track controlling field errors to avoid duplicates

    problematicFields.forEach((field) => {
      // Error message for the problematic field itself (generic, without field names)
      const fieldErrorMessage = field.constraintType === 'visibility'
        ? `This field is currently hidden but contains a value. Please clear this value or adjust the form to make it visible.`
        : `This field is currently disabled but contains a value. Please clear this value or adjust the form to enable it.`;

      // Set error on problematic field
      if (field.categoryKey) {
        if (!newErrors[field.categoryKey]) {
          newErrors[field.categoryKey] = {};
        }
        newErrors[field.categoryKey][field.fieldKey] = fieldErrorMessage;
      } else {
        newErrors[field.fieldKey] = fieldErrorMessage;
      }
      fieldsToTouch.push(field.fieldPath);

      // Set error on controlling fields
      if (field.controllingFields && field.controllingFields.length > 0) {
        field.controllingFields.forEach((controllingField) => {
          // Only set error on controlling fields that are ACTUALLY causing the disabled/hidden state
          // Skip fields where causesDisabled is false - they are not responsible for the current state
          if (!controllingField.causesDisabled) {
            return; // Skip this controlling field
          }
          
          const controllingPath = controllingField.fieldPath;
          
          // Build a generic error message for the controlling field (without exposing field names)
          const controllingErrorMessage = field.constraintType === 'visibility'
            ? `This field's value is hiding another field which contains data. Please clear the hidden field or change this value.`
            : `This field's value is disabling another field which contains data. Please clear the disabled field or change this value.`;

          // Only set if not already set (avoid duplicate messages)
          if (!controllingFieldErrors.has(controllingPath)) {
            controllingFieldErrors.set(controllingPath, controllingErrorMessage);
          }
          
          // Add to fields to touch
          if (!fieldsToTouch.includes(controllingPath)) {
            fieldsToTouch.push(controllingPath);
          }
        });
      }
    });

    // Add controlling field errors to newErrors object
    controllingFieldErrors.forEach((errorMessage, fieldPath) => {
      const pathParts = fieldPath.split('.');
      if (pathParts.length === 2) {
        // Nested field (category.field)
        const [categoryKey, fieldKey] = pathParts;
        if (!newErrors[categoryKey]) {
          newErrors[categoryKey] = {};
        }
        // Only set if not already set (problematic field errors take precedence)
        if (!newErrors[categoryKey][fieldKey]) {
          newErrors[categoryKey][fieldKey] = errorMessage;
        }
      } else {
        // Root-level field
        if (!newErrors[fieldPath]) {
          newErrors[fieldPath] = errorMessage;
        }
      }
    });

    // Merge with existing errors
    const mergedErrors = mergeErrors(currentErrors || {}, newErrors);
    
    // Set errors
    setErrors(mergedErrors);

    // Mark all affected fields as touched so errors are visible
    // Use setTimeout to ensure state updates complete before touching fields
    setTimeout(() => {
      fieldsToTouch.forEach((fieldPath) => {
        if (formikHelpersRef.current) {
          formikHelpersRef.current.setFieldTouched(fieldPath, true, false);
        }
      });

      // Focus on the first error field after a short delay to ensure DOM is updated
      setTimeout(() => {
        if (fieldsToTouch.length > 0) {
          const firstFieldPath = fieldsToTouch[0];
          // Build selector for the field - escape dots for CSS selector
          const selector = `[name="${firstFieldPath.replace(/\./g, '\\.')}"]`;
          const errorElement = document.querySelector(selector);
          
          if (errorElement) {
            errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            errorElement.focus({ preventScroll: true });
          }
        }
      }, 100);
    }, 0);
  };

  /**
   * Handler for dependsOn validation dialog confirm - clear problematic field values and save
   * 
   * This function clears all values from hidden/disabled fields that were causing validation
   * issues, then proceeds with the form submission.
   * 
   * @param {Array<ProblematicField>} problematicFields - Array of fields to clear
   */
  const handleDependsOnClearAndSave = (problematicFields) => {
    if (!problematicFields || problematicFields.length === 0 || !backupValues) {
      setDependsOnValidationDialog({ 
        open: false, 
        problematicFields: [],
      });
      return;
    }

    // Create a deep copy of current values to modify
    const clearedValues = _.cloneDeep(backupValues);

    // Clear each problematic field's value
    problematicFields.forEach((field) => {
      const emptyValue = getEmptyValueForFieldType(field.fieldType);
      
      if (field.categoryKey) {
        // Field is within a category
        if (clearedValues[field.categoryKey]) {
          clearedValues[field.categoryKey][field.fieldKey] = emptyValue;
        }
      } else {
        // Root-level field
        clearedValues[field.fieldKey] = emptyValue;
      }

      console.debug('[DynamicForm] handleDependsOnClearAndSave: Clearing field', {
        fieldPath: field.fieldPath,
        fieldType: field.fieldType,
        oldValue: field.currentValue,
        newValue: emptyValue,
      });
    });

    // Update form state if formikHelpers is available
    if (formikHelpersRef.current) {
      const { setFieldValue, setErrors, setFieldTouched, errors: currentErrors } = formikHelpersRef.current;
      
      // Clear field values in Formik
      problematicFields.forEach((field) => {
        const emptyValue = getEmptyValueForFieldType(field.fieldType);
        setFieldValue(field.fieldPath, emptyValue);
        setFieldTouched(field.fieldPath, false);
      });

      // Clear errors for the problematic fields
      if (currentErrors) {
        const updatedErrors = { ...currentErrors };
        let hasChanges = false;

        problematicFields.forEach((field) => {
          if (field.categoryKey) {
            if (updatedErrors[field.categoryKey] && updatedErrors[field.categoryKey][field.fieldKey] !== undefined) {
              delete updatedErrors[field.categoryKey][field.fieldKey];
              hasChanges = true;
              // Clean up empty category error objects
              if (Object.keys(updatedErrors[field.categoryKey]).length === 0) {
                delete updatedErrors[field.categoryKey];
              }
            }
          } else {
            if (updatedErrors[field.fieldKey] !== undefined) {
              delete updatedErrors[field.fieldKey];
              hasChanges = true;
            }
          }
        });

        if (hasChanges) {
          setErrors(updatedErrors);
        }
      }
    }

    // Close dialog
    setDependsOnValidationDialog({ 
      open: false, 
      problematicFields: [],
    });

    // Update backup values and proceed with submission
    setBackupValues(clearedValues);

    // Continue with the normal submission flow using cleared values
    if (_config.otpRequired) {
      generateOTP();
      setOpenOTP(true);
    } else if (sectionsLength === sectionIndex) {
      setConfirmDialog({
        open: true,
        message: _config?.submitConfirmationMessage || 'Are you sure you want to submit the details?',
        buttonLabel: 'Submit',
      });
    } else {
      _handleSubmit(clearedValues);
    }
  };

  return (
    <>
      {_config && initialValuess && (
        <Formik
          validateOnChange={false}
          validateOnBlur={true}
          initialValues={initialValuess}
          validationSchema={validationSchema}
          enableReinitialize={true}
          onSubmit={checkSectionIndex}
        >
          {({ isSubmitting, setFieldValue, values, touched, errors, setErrors, setFieldTouched, ...formikHelpers }) => {
            // Store formikHelpers in ref for useEffect access
            // Include explicitly destructured props since they won't be in ...formikHelpers
            formikHelpersRef.current = { 
              ...formikHelpers, 
              errors, 
              setFieldValue, 
              setErrors, 
              setFieldTouched 
            };

            return (
              <Form>
                {_config.categories && (
                  <DynamicCategory
                    stepStatusData={stepStatusData}
                    _config={_config.categories}
                    setFieldValue={setFieldValue}
                    values={values}
                    touched={touched}
                    handleAddNewField={(parentKey, keys, values) => handleAddNewField(parentKey, keys, values, errors)}
                    errors={errors}
                    userSiteTrialRole={userSiteTrialRole}
                    otpRequired={_config.otpRequired}
                    readOnly={readOnly || showSkeleton}
                    showSkeleton={showSkeleton}
                    handleSaveSignature={handleSaveSignature}
                    handleUploadFile={handleUploadFile}
                    handleDownloadFile={handleDownloadFile}
                    handleDownloadSignature={handleDownloadSignature}
                    sectionKey={sectionKey}
                    subSectionKey={subSectionKey}
                    removeCustomField={handleRemoveCustomField}
                    openOTP={openOTP}
                    queriesByFieldKey={queriesByFieldKey}
                    formConfig={formConfig}
                  />
                )}
              {_config.fields && (
                <DynamicFields
                  stepStatusData={stepStatusData}
                  _config={_config.fields}
                  setFieldValue={setFieldValue}
                  values={values}
                  touched={touched}
                  userSiteTrialRole={userSiteTrialRole}
                  errors={errors}
                  otpRequired={_config.otpRequired}
                  readOnly={readOnly || showSkeleton}
                  showSkeleton={showSkeleton}
                  handleSaveSignature={handleSaveSignature}
                  handleUploadFile={handleUploadFile}
                  handleDownloadFile={handleDownloadFile}
                  handleDownloadSignature={handleDownloadSignature}
                  sectionKey={sectionKey}
                  subSectionKey={subSectionKey}
                  openOTP={openOTP}
                  queriesByFieldKey={queriesByFieldKey}
                  formConfig={formConfig}
                />
              )}
              {showButton && buttonLabel && !openOTP && (
                <Box m={2} sx={{ display: 'flex', justifyContent: 'end' }}>
                  <CustomButton type="submit" variant="contained" color="primary" disabled={false}>
                    {buttonLabel}
                  </CustomButton>
                </Box>
              )}
              {openOTP && (
                <Box m={2} sx={{ display: 'flex', justifyContent: 'end' }}>
                  <OTPPanel otpVerified={verifyOTP} />
                </Box>
              )}
                <QuizDrawer />
                <FocusError />
              </Form>
            );
          }}
        </Formik>
      )}
      {/* DependsOn validation dialog for hidden/disabled fields with values */}
      <DependsOnValidationDialog
        open={dependsOnValidationDialog.open}
        problematicFields={dependsOnValidationDialog.problematicFields}
        onClose={handleDependsOnValidationDialogClose}
        onClearAndSave={handleDependsOnClearAndSave}
        title="Hidden or Disabled Fields Contain Values"
        reviewButtonLabel="Review Form"
        clearAndSaveButtonLabel="Clear Values and Save"
      />
      {/* Confirmation dialog for field-specific validation confirmations */}
      <ConfirmationDialog
        open={confirmationValidationDialog.open}
        title={confirmationValidationDialog.title}
        buttonLabel={confirmationValidationDialog.buttonLabel}
        cancelButtonLabel={confirmationValidationDialog.cancelButtonLabel}
        message={confirmationValidationDialog.message}
        handleClose={handleConfirmationValidationDialogClose}
        handleConfirm={handleConfirmationValidationDialogConfirm}
      />
      {/* Balancing criteria exceeded dialog */}
      <ConfirmationDialog
        open={balancingCriteriaDialog.open}
        title={balancingCriteriaDialog.title}
        buttonLabel={balancingCriteriaDialog.buttonLabel}
        cancelButtonLabel={balancingCriteriaDialog.cancelButtonLabel}
        message={balancingCriteriaDialog.message}
        handleClose={handleBalancingCriteriaDialogClose}
        handleConfirm={handleBalancingCriteriaDialogConfirm}
      />
      {/* Standard confirmation dialog for final submission */}
      <ConfirmationDialog
        open={confirmDialog.open}
        buttonLabel={confirmDialog.buttonLabel}
        message={confirmDialog.message}
        handleClose={handleConfirmDialogClose}
        handleConfirm={() => _handleSubmit(backupValues)}
      />
    </>
  );
}
export default React.memo(DynamicForm);
